import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Plus, MoreVertical, Edit, Trash2, X, XCircle, Check,
  ShoppingCart, ImageOff, Minus, CreditCard, Banknote, QrCode, ArrowLeft,
  Receipt, User, AlertTriangle, ChevronRight, ChevronDown, Printer, RotateCcw,
  ArrowRightLeft, Users, Layers, UserPlus, Ticket, Clock, CheckCircle2, ScanLine,
  Eye,
} from "lucide-react";
import ProductPopupCardapio from "../components/ProductPopupCardapio";
import { usePizzaPopup } from "../components/pizza/usePizzaPopup";
import { AvisoOfertasCliente, useOfertasNoCarrinho } from "../features/ofertas-do-dia";
import CartItemCard from "../components/cart/CartItemCard";
import TrocoModal from "../components/TrocoModal";
import PixGerarQrModal from "../components/PixGerarQrModal";
import PixCobrancaModal from "../features/pix-cobranca/PixCobrancaModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { CarregandoGrade } from "../components/carregando";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
} from "../components/ui/sheet";
import { imprimirCupomFechamento } from "../utils/cupomFechamento";
import { servidorImprimeCaixa } from "../utils/cupomDelivery";
import { imprimirCupomPreparo } from "../utils/cupomPreparo";
import { linhasDaEtapa } from "../utils/precoEtapas";
import { temPersonalizacao } from "../utils/cartGrouping";
import { ComandaMesaDialog } from "../features/mesas-comanda";
import { SEM_CAIXA, caixaAbertoAgora, lancarVendasNoCaixa } from "../features/mesas-fechamento/lib/vendaNoCaixa";
import { dividirParaCancelar } from "../features/mesas-lancar/cancelarParte";
import { ConfirmarCancelarItem, DesativarTodosPreparo, ItensCancelados, ResumoInfoMesa, SairSemEnviarDialog, comandaEmUso, contextoDoCancelamento, guardaInfo, infoEditada, infoMudou, miniaturaDe, obsDoPedidoMudou, observacaoDoPedido } from "../features/mesas-lancar";
import { usePreparoAtivo } from "../features/preferencias";
import DialogQrCodesEmMassa from "../features/mesa-qr/DialogQrCodesEmMassa";
import DialogQrMesa from "../features/mesa-qr/DialogQrMesa";
import DonoDoItem from "../features/mesa-qr/DonoDoItem";
import FaixaQrSemCaixa from "../features/mesa-qr/FaixaQrSemCaixa";
import NovaPessoaDialog from "../features/mesa-qr/NovaPessoaDialog";
import PagarPorPessoa from "../features/mesa-qr/PagarPorPessoa";
import QuemPediu, { quemPediu } from "../features/mesa-qr/QuemPediu";
import { useCobrancaPorPessoa } from "../features/mesa-qr/useCobrancaPorPessoa";
import { useDonosCarrinho } from "../features/mesa-qr/useDonosCarrinho";
import { useSaidaQrLigada } from "../features/mesa-qr/useMesaQrLigado";
import { usePerguntaNomeQr } from "../features/mesa-qr/usePerguntaNomeQr";
import LerQrSaida from "../features/mesa-saida/LerQrSaida";
import { casaPeloCodigo } from "../lib/codigoBarras";
import { formatarQtd, medidaDoItem } from "../lib/unidadeVenda";
import {
  FechamentoMesaDialog,
  valorItem,
  pagoParcialItem,
  criarDivisao,
  parteAtual,
  aplicarParte,
  somarDesconto,
  restaurarParcial,
  montarPagamentos,
  descontoDoFechamento,
  descontoPorPedido,
  valorSelecionado,
  partesPorPedido,
  editarParte,
  desfazerPagamento,
  efeitoNosItens,
  novoIdPagamento,
  contaDaMesa,
  pagoDoItem,
  lerPagamentos,
  lancarRodada,
  desfazerNaRodada,
  descontoJaConcedido,
  quitadoDaMesa,
  SeloPagoItem,
  ResumoContaMesa,
  FecharReaberta,
  itensDaRodada,
  useCupomDaRodada,
  descontoEmAndamento,
} from "../features/mesas-fechamento";
import { SeloItemMesa, useSituacaoItensMesa } from "../features/kds";
import { motivoValido } from "../utils/motivoCancelamento";
import { pizzaMetaDoItem, subitemsComPizza } from "../utils/pizzaNoPedido";
import { useTecladoMesas } from "../features/mesas-teclado";

// URL relativa - funciona em qualquer domínio
const API = '/api';

// Quilo/litro (30/09/2026): produto por peso conta 1 item no "N itens" —
// 1,250 kg de picanha não é "1,25 itens". Por unidade soma a quantidade.
const contarItens = (items, produtos) => (items || [])
  .filter((i) => !i.deleted)
  .reduce((s, i) => s + (medidaDoItem(i, produtos) ? 1 : Number(i.quantity || i.quantidade || 1)), 0);

// id do produto no item que vai para o pedido (30/09/2026). O pedido não guarda
// a unidade de venda: quilo/litro, preparo e relatórios acham o produto pelo id.
const idDoProduto = (i) => {
  const pid = i.id ?? i.product_id ?? null;
  return pid ? { id: pid, product_id: pid } : {};
};

// Visual por forma de pagamento quando a API não manda icon/color (v1.5.516):
// sem isto os botões do Fechar Mesa saíam todos com o MESMO ícone cinza.
const pmVisual = (code = "", label = "") => {
  const k = `${code} ${label}`.toLowerCase();
  if (/dinheiro|especie/.test(k)) return { icon: Banknote, color: "bg-green-500" };
  if (/pix/.test(k)) return { icon: QrCode, color: "bg-teal-500" };
  if (/credito|crédito/.test(k)) return { icon: CreditCard, color: "bg-blue-500" };
  if (/debito|débito/.test(k)) return { icon: CreditCard, color: "bg-purple-500" };
  if (/cart/.test(k)) return { icon: CreditCard, color: "bg-blue-500" };
  if (/vale|[^a-z]vr[^a-z]|[^a-z]va[^a-z]/.test(` ${k} `)) return { icon: Ticket, color: "bg-amber-500" };
  return { icon: CreditCard, color: "bg-slate-500" };
};

const fallbackPaymentMethods = [
  { id: "dinheiro", code: "dinheiro", label: "Dinheiro", icon: Banknote, color: "bg-green-500" },
  { id: "credito", code: "credito", label: "Crédito", icon: CreditCard, color: "bg-blue-500" },
  { id: "debito", code: "debito", label: "Débito", icon: CreditCard, color: "bg-purple-500" },
  { id: "pix", code: "pix", label: "PIX", icon: QrCode, color: "bg-teal-500" },
];

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `/api${url.startsWith("/") ? url : "/" + url}`;
};

const isPngImage = (url) => {
  if (!url) return false;
  return url.toLowerCase().endsWith('.png');
};

// Mesmo critério do backend/Delivery: # + 5 dígitos
const generateMesaCodigo = () => "#" + String(Math.floor(Math.random() * 100000)).padStart(5, "0");

// Label curto para o "número grande" do card. Permite labels tipo "1A", "1B" ao usar
// nome como "Mesa 1A" — sem precisar mexer no schema (numero continua INT).
const mesaLabelShort = (mesa) => {
  if (mesa && mesa.nome) {
    const m = String(mesa.nome).match(/^Mesa\s+(.+)$/i);
    if (m) {
      const v = m[1].trim();
      // Se for puramente numerico, normaliza para 2 digitos pra preservar UX antiga
      if (/^\d+$/.test(v)) return v.padStart(2, "0");
      return v.toUpperCase();
    }
  }
  return String((mesa && mesa.numero) || 0).padStart(2, "0");
};

const formatMesaTimer = (opened_at) => {
  if (!opened_at) return "—";
  // Garante que a data seja tratada como UTC (adiciona 'Z' se não tiver)
  let dateStr = String(opened_at);
  if (dateStr.includes("T") && !dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-", 10)) {
    dateStr = dateStr + "Z";
  }
  const s = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (s < 0) return "< 1 min"; // Proteção contra datas futuras
  if (s < 60) return "< 1 min";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}min`;
};

// Recalcula o total de uma mesa excluindo itens deletados (inclui subitems)
const recalcularTotalMesa = (mesa) => {
  if (!mesa || !mesa.items || mesa.items.length === 0) return 0;
  return mesa.items.reduce((sum, item) => {
    if (item.deleted) return sum;
    const qty = item.quantity || 1;
    const itemTotal = (item.final_price ?? item.sale_price ?? 0) * qty;
    return sum + itemTotal;
  }, 0);
};

/** Mesas chegando na 1ª abertura (dono, 25/10/2026): o título da seção e os
 * cartões quadrados de "Mesas Disponíveis" em cinza, no lugar de "Mesas
 * Disponíveis (0) · Todas as mesas estão ocupadas" antes da hora. */
function MesasCarregando() {
  return (
    <div data-e2e="mesas-carregando" aria-busy="true" aria-label="Carregando">
      <Skeleton className="h-6 w-56 mb-3" />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(73px,112px))] gap-1.5 sm:gap-2 md:gap-3">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className="relative rounded-lg border bg-card overflow-hidden">
            <div style={{ paddingBottom: "102%" }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
              <Skeleton className="h-2.5 w-8" />
              <Skeleton className="h-6 w-9" />
              <Skeleton className="h-3.5 w-12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Componente de Card da Mesa — Status, Nº, Comanda, Cliente; código, timer, total, reimpressão; Cancelar Mesa (igual Delivery)
// isMobile: melhora touch (botões 44px), remove scale em fechadas no mobile
function MesaCard({ mesa, onClick, onVerComanda, onEdit, onDelete, onCancelarMesa, onLiberarMesa, onFecharMesa, onFecharSemImprimir, onTransferirMesa, onConfirmarPagamento, onReabrirMesa, onImprimirAmbos, onImprimirFechamento, onImprimirPreparo, onQrCode, isMobile, produtos }) {
  const isOpen = mesa.status === "aberta";
  const isFechamento = mesa.status === "fechamento";
  // Olhinho da comanda: com produto lançado, mesmo que cancelado — a comanda
  // mostra os cancelados (dono, 25/09/2026)
  const itensLancados = (mesa?.items) || [];
  const temComanda = !!onVerComanda && itensLancados.length > 0;
  // Mesa aberta com todos os produtos cancelados: no lugar de Imprimir/Fechar
  // mesa vem "Liberar mesa" (dono, 25/09/2026 — caso Mesa 1 da Point)
  const tudoCancelado = itensLancados.length > 0 && itensLancados.every((i) => i.deleted);
  const hasComanda = mesa.comanda != null && String(mesa.comanda).trim() !== "";
  // Pagamento que o cliente fez pelo celular: a conta já está quitada e o
  // salão só confere. `pagamentos.log` é o mesmo que a tela de fechamento lê.
  const pagoNoCelular = (() => {
    const pg = mesa.pagamentos;
    if (!pg || typeof pg !== "object") return null;
    const total = Number(mesa.total || 0);
    const pago = Number(pg.partial_paid || 0);
    if (!(pago > 0 && pago >= total - 0.01)) return null;
    const doQr = (pg.log || []).find((l) => l && l.origem === "mesa_qr");
    return doQr ? doQr.forma || "PIX Online" : null;
  })();
  // Parte da conta já paga (Fechar mesa, celular): o cartão mostra "Pago R$ X
  // · falta R$ Y" mesmo com a mesa reaberta (dono, 24/09/2026)
  const contaCartao = contaDaMesa(mesa);
  const pagoCartao = contaCartao.pago > 0.004 ? contaCartao : null;
  const btnIcon = isMobile ? "h-11 w-11 min-h-[44px] min-w-[44px]" : "h-8 w-8";

  // Card quadrado para mesa fechada
  if (!isOpen && !isFechamento) {
    return (
      <div
        className="m-0 relative rounded-lg border overflow-hidden transition-all duration-200 bg-card shadow-sm hover:border-primary/50 cursor-pointer"
        onClick={() => onClick(mesa)}
      >
        <div style={{ paddingBottom: '102%' }} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="absolute top-1 right-1 z-10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-e2e="mesa-menu" className={`${btnIcon} touch-manipulation`} onClick={(e) => e.stopPropagation()}>
                  <MoreVertical className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(mesa); }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Mesa
                </DropdownMenuItem>
                {onQrCode && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onQrCode(mesa); }} data-e2e="menu-qrcode">
                    <QrCode className="w-4 h-4 mr-2" />
                    QR Code da mesa
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onDelete(mesa); }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Deletar Mesa
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Mesa</span>
          <span className="text-2xl font-bold leading-none mt-0.5">{mesaLabelShort(mesa)}</span>
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground mt-1">Fechada</span>
        </div>
      </div>
    );
  }

  // Card para mesa em fechamento (aguardando pagamento)
  if (isFechamento) {
    return (
      <div className="m-0 relative rounded-xl border overflow-hidden transition-all duration-200 border-amber-500 shadow-lg shadow-amber-500/20 bg-amber-50/50 dark:bg-amber-950/30 hover:shadow-xl hover:shadow-amber-500/25 cursor-pointer ring-2 ring-amber-500/30">
        <div className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-2 gap-1">
            <span className="font-mono text-[10px] sm:text-xs text-muted-foreground truncate">{mesa.codigo || "—"}</span>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[10px] sm:text-xs text-muted-foreground">{formatMesaTimer(mesa.opened_at)}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button onClick={(e) => e.stopPropagation()} data-e2e="mesa-menu"
                    className={`rounded-md hover:bg-muted transition-colors touch-manipulation ${isMobile ? "p-2 min-h-[40px] min-w-[40px] flex items-center justify-center" : "p-1.5"}`}>
                    <MoreVertical className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onReabrirMesa && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onReabrirMesa(mesa); }}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reabrir Mesa
                    </DropdownMenuItem>
                  )}
                  {onTransferirMesa && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onTransferirMesa(mesa); }}>
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Transferir Mesa
                    </DropdownMenuItem>
                  )}
                  {onImprimirFechamento && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onImprimirFechamento(mesa); }}>
                      <Printer className="w-4 h-4 mr-2" />
                      Reimprimir Fechamento
                    </DropdownMenuItem>
                  )}
                  {onQrCode && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onQrCode(mesa); }} data-e2e="menu-qrcode">
                      <QrCode className="w-4 h-4 mr-2" />
                      QR Code da mesa
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {onCancelarMesa && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCancelarMesa(mesa); }} className="text-red-600 focus:text-red-600 dark:text-red-400">
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancelar Mesa
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Cliente que pagou pelo celular (Mesa QR) chega aqui já quitado: o
              selo vira PAGO com a forma, e o salão só confere e encerra. */}
          {pagoNoCelular ? (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold mb-2 sm:mb-3 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <CheckCircle2 className="w-3 h-3" />
              PAGO · {pagoNoCelular}
            </div>
          ) : (
            <div className="inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium mb-2 sm:mb-3 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 animate-pulse">
              {mesa.pagamentos?.reaberta ? "Reaberta · aguardando pagamento" : "Aguardando Pagamento"}
            </div>
          )}

          <div className="text-center mb-1">
            <p className="text-[10px] uppercase text-muted-foreground mb-0.5">Mesa</p>
            <span className="text-xl sm:text-2xl font-bold">{mesaLabelShort(mesa)}</span>
          </div>
          {hasComanda && (
            <div className="text-center mb-1">
              <p className="text-[10px] uppercase text-muted-foreground mb-0.5">Comanda</p>
              <span className="text-xl sm:text-2xl font-bold">#{String(mesa.comanda)}</span>
            </div>
          )}
          <p className="text-[10px] sm:text-xs text-muted-foreground text-center mb-2 sm:mb-3 truncate">{mesa.nome && /^Mesa\s+\S/i.test(mesa.nome) ? '' : (mesa.nome || `Mesa ${mesa.numero}`)}</p>

          <div className="space-y-1 text-xs sm:text-sm border-t pt-2 sm:pt-3">
            <div className="flex justify-between items-center gap-1">
              <span className="text-muted-foreground shrink-0">Cliente:</span>
              <span className="font-medium truncate text-right">{mesa.cliente || "—"}</span>
            </div>
            {mesa.pessoas > 0 && (
              <div className="flex justify-between items-center gap-1">
                <span className="text-muted-foreground shrink-0">Pessoas:</span>
                <span className="font-medium">{mesa.pessoas}</span>
              </div>
            )}
            {mesa.observacao?.trim() && (
              <div className="flex items-start gap-1.5 text-[11px] sm:text-xs bg-amber-100/70 dark:bg-amber-900/25 text-amber-800 dark:text-amber-200 rounded px-2 py-1 border border-amber-300/60 dark:border-amber-700/40 leading-snug break-words">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                <span className="break-words">{mesa.observacao.trim()}</span>
              </div>
            )}
            <div className="flex justify-between text-primary font-bold pt-1">
              <span>Total:</span>
              <span className="truncate">R$ {(mesa.total || 0).toFixed(2).replace('.', ',')}</span>
            </div>
            {pagoCartao && !pagoNoCelular && (
              <>
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold" data-e2e="cartao-pago">
                  <span>Pago:</span>
                  <span className="truncate">R$ {pagoCartao.pago.toFixed(2).replace('.', ',')}</span>
                </div>
                <div className="flex justify-between font-bold" data-e2e="cartao-falta">
                  <span>Falta:</span>
                  <span className="truncate">R$ {pagoCartao.restante.toFixed(2).replace('.', ',')}</span>
                </div>
              </>
            )}
          </div>

          <div className="mt-3 sm:mt-4">
            <Button
              onClick={(e) => { e.stopPropagation(); onConfirmarPagamento(mesa); }}
              className={`w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold touch-manipulation text-xs sm:text-sm ${isMobile ? "h-11 min-h-[44px]" : "h-10"}`}
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" />
              FECHAR MESA
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Contador de itens (somando quantidades, ignorando deletados; peso conta 1)
  const itemsCount = Array.isArray(mesa.items) ? contarItens(mesa.items, produtos) : 0;

  return (
    <div
      className={`
        m-0 relative rounded-xl border overflow-hidden
        transition-all duration-200
        border-green-500/70 shadow-md shadow-green-500/15 bg-card hover:shadow-lg hover:shadow-green-500/20 ring-1 ring-green-500/25
      `}
    >
      <div className="p-3 sm:p-4">
        {/* Observação da mesa: cartão compacto no topo (dono, 25/09/2026) —
            antes ficava no meio do cartão e passava despercebida */}
        {mesa.observacao?.trim() && (
          <div className="-mt-1 mb-2 flex items-start gap-1.5 rounded-md bg-amber-400/15 border border-amber-400/50 text-amber-700 dark:text-amber-300 px-2 py-1 text-[11px] font-semibold leading-snug"
            title={mesa.observacao.trim()} data-e2e="mesa-obs-card">
            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
            <span className="line-clamp-2 break-words">{mesa.observacao.trim()}</span>
          </div>
        )}

        {/* Header: codigo + tempo (neutro, sem cor de urgencia) + menu */}
        {isOpen && (
          <div className="flex justify-between items-center gap-1 mb-3">
            <span className="font-mono text-[10px] sm:text-[11px] text-muted-foreground truncate">{mesa.codigo || "—"}</span>
            <div className="flex items-center gap-1 shrink-0">
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Tempo aberta">
                <Clock className="w-3 h-3" />
                {formatMesaTimer(mesa.opened_at)}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    data-e2e="mesa-menu"
                    className={`rounded-md hover:bg-muted transition-colors touch-manipulation ${isMobile ? "p-2 min-h-[40px] min-w-[40px] flex items-center justify-center" : "p-1.5"}`}
                    title="Mais opções"
                  >
                    <MoreVertical className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(onImprimirAmbos || onImprimirFechamento || onImprimirPreparo) && (
                    <>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onImprimirAmbos?.(mesa); }}>
                        <Printer className="w-4 h-4 mr-2" /> 2ª via de ambos
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onImprimirFechamento?.(mesa); }}>
                        <Printer className="w-4 h-4 mr-2" /> 2ª via Fechamento
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onImprimirPreparo?.(mesa); }}>
                        <Printer className="w-4 h-4 mr-2" /> 2ª via Preparo
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {onQrCode && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onQrCode(mesa); }} data-e2e="menu-qrcode">
                      <QrCode className="w-4 h-4 mr-2" />
                      QR Code da mesa
                    </DropdownMenuItem>
                  )}
                  {onTransferirMesa && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onTransferirMesa(mesa); }}>
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Transferir Mesa
                    </DropdownMenuItem>
                  )}
                  {onCancelarMesa && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); onCancelarMesa(mesa); }}
                        className="text-red-600 focus:text-red-600 dark:text-red-400"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancelar Mesa
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {/* HERO: numero da mesa */}
        <div className="text-center mb-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Mesa</p>
          <div className="text-4xl sm:text-5xl font-black leading-none text-foreground tabular-nums">{mesaLabelShort(mesa)}</div>
          {hasComanda && (
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold">
              <Layers className="w-3 h-3" /> Comanda #{String(mesa.comanda).trim()}
            </div>
          )}
        </div>

        {/* Meta: cliente + pessoas (so se preenchidos) */}
        {(mesa.cliente || mesa.pessoas > 0) && (
          <div className="flex items-center justify-center gap-3 mb-2 text-[11px] text-muted-foreground flex-wrap">
            {mesa.cliente && (
              <span className="inline-flex items-center gap-1 truncate max-w-full">
                <User className="w-3 h-3 shrink-0" />
                <span className="truncate font-medium text-foreground">{mesa.cliente}</span>
              </span>
            )}
            {mesa.pessoas > 0 && (
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" />
                {mesa.pessoas}
              </span>
            )}
          </div>
        )}


        {/* Total + qtd em uma unica linha (sem quebrar) */}
        <div className="text-center mb-3">
          <div className="text-lg sm:text-xl font-black text-orange-600 dark:text-orange-400 leading-tight tabular-nums whitespace-nowrap">
            R$ {(mesa.total || 0).toFixed(2).replace('.', ',')}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {itemsCount > 0 ? `${itemsCount} ${itemsCount === 1 ? "item" : "itens"}` : "Sem itens"}
          </div>
          {pagoCartao && (
            <div className="text-[11px] font-semibold leading-tight mt-0.5" data-e2e="cartao-pago">
              <span className="text-emerald-600 dark:text-emerald-400">Pago R$ {pagoCartao.pago.toFixed(2).replace('.', ',')}</span>
              <span className="text-muted-foreground"> · falta R$ {pagoCartao.restante.toFixed(2).replace('.', ',')}</span>
            </div>
          )}
        </div>

        {/* Acao primaria: ADICIONAR ITENS — uma linha so. O olhinho à esquerda
            abre a comanda da mesa (dono, 25/09/2026) */}
        <div className="flex gap-1.5">
          {temComanda && (
            <Button
              type="button"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onVerComanda(mesa); }}
              className="shrink-0 h-auto px-2.5 py-2.5 touch-manipulation"
              title="Ver a comanda da mesa"
              aria-label="Ver a comanda da mesa"
              data-e2e="mesa-ver-comanda"
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}
          {/* Com o olhinho do lado o cartão fica apertado: letra menor e sem o "+" */}
          <Button
            onClick={(e) => { e.stopPropagation(); onClick(mesa); }}
            className={`flex-1 min-w-0 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold touch-manipulation h-auto py-2.5 whitespace-nowrap leading-tight shadow-sm ${temComanda ? "text-xs px-2" : "text-xs sm:text-sm"}`}
          >
            {!temComanda && <Plus className="w-4 h-4 mr-1 shrink-0" />}
            ADICIONAR ITENS
          </Button>
        </div>

        {/* Ações de fechamento — so' com itens LANÇADOS (v1.5.469). Duas
            (v1.5.515, pedido do dono): IMPRIMIR faz o de sempre (cupom + vai
            pro fechamento); FECHAR MESA pula direto pro fechamento SEM cupom. */}
        {tudoCancelado && onLiberarMesa && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onLiberarMesa(mesa); }}
            className="w-full mt-1.5 inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400 py-1.5 rounded-md hover:bg-red-500/10 transition-colors touch-manipulation whitespace-nowrap"
            title="Todos os produtos foram cancelados — cancela a mesa e deixa ela livre"
            data-e2e="mesa-liberar"
          >
            <XCircle className="w-3.5 h-3.5 shrink-0" />
            Liberar mesa
          </button>
        )}
        {((mesa?.items) || []).filter(i => !i.deleted).length > 0 && (
        <div className="w-full mt-1.5 grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFecharMesa(mesa); }}
            className="inline-flex items-center justify-center gap-1 text-[10px] min-[380px]:text-[11px] text-muted-foreground hover:text-foreground py-1.5 rounded-md hover:bg-muted/60 transition-colors touch-manipulation whitespace-nowrap"
            title="Imprime o cupom de fechamento e leva a mesa pro pagamento"
          >
            <Printer className="w-3.5 h-3.5 shrink-0" />
            Imprimir
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFecharSemImprimir && onFecharSemImprimir(mesa); }}
            className="inline-flex items-center justify-center gap-1 text-[10px] min-[380px]:text-[11px] text-muted-foreground hover:text-foreground py-1.5 rounded-md hover:bg-muted/60 transition-colors touch-manipulation whitespace-nowrap"
            title="Vai direto pro pagamento, sem imprimir cupom"
          >
            <XCircle className="w-3.5 h-3.5 shrink-0" />
            Fechar mesa
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// Componente Principal - Mesas
export default function Mesas({ setHeaderHidden, qrSemCaixa = false, onAbrirCaixa } = {}) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = () => setIsMobile(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const [mesas, setMesas] = useState([]);
  // Cards cinza só até a 1ª leitura das mesas; o polling de 5 s não volta a eles
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [comandaMesa, setComandaMesa] = useState(null); // mesa com a comanda aberta (olhinho)
  const [sairSemEnviarOpen, setSairSemEnviarOpen] = useState(false); // Voltar com carrinho não enviado
  const [itemParaCancelar, setItemParaCancelar] = useState(null); // { idx, item } — confirmação da lixeira
  // Loja sem cupom de preparo (Preferências): o ENVIAR manda direto, sem a conferência de impressão
  const preparoAtivo = usePreparoAtivo();
  const [searchTerm, setSearchTerm] = useState("");
  // Mesa cujo QR Code está aberto (pedido pelo celular do cliente).
  const [mesaQrSelecionada, setMesaQrSelecionada] = useState(null);
  const [qrEmMassa, setQrEmMassa] = useState(false);
  const [mesaDialogOpen, setMesaDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMesa, setCurrentMesa] = useState(null);
  const [mesaNome, setMesaNome] = useState("");
  const [mesaNumero, setMesaNumero] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mesaToDelete, setMesaToDelete] = useState(null);
  const [cancelarMesaModalOpen, setCancelarMesaModalOpen] = useState(false);
  const [mesaParaCancelar, setMesaParaCancelar] = useState(null);
  const [motivoCancelamentoMesa, setMotivoCancelamentoMesa] = useState("");

  // Transferir mesa
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [mesaOrigem, setMesaOrigem] = useState(null);
  const [mesaDestinoId, setMesaDestinoId] = useState("");

  // Pessoas por mesa
  const [pessoasVal, setPessoasVal] = useState("");
  
  // Tela de vendas
  const [mesaVendasOpen, setMesaVendasOpen] = useState(false);
  const [selectedMesa, setSelectedMesa] = useState(null);
  // Selo por item na comanda (em produção / pronto / entregue), vindo da
  // marcação da cozinha e do garçom no KDS.
  const { situacaoDe } = useSituacaoItensMesa(selectedMesa?.id);
  const [mesaObservacao, setMesaObservacao] = useState("");

  // Esconder header superior enquanto a tela de vendas estiver aberta (libera espaço no mobile)
  useEffect(() => {
    if (typeof setHeaderHidden !== "function") return;
    setHeaderHidden(mesaVendasOpen);
    return () => setHeaderHidden(false);
  }, [mesaVendasOpen, setHeaderHidden]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // todos os produtos (incl. order_steps) para preços automáticos
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [activePromotions, setActivePromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clienteNome, setClienteNome] = useState("");
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  // Campanha Inteligente: ofertas do cliente da mesa aplicadas nos itens a enviar.
  const ofertasCliente = useOfertasNoCarrinho({ clienteId: selectedClienteId, modulo: "Mesas", cart, setCart, produtos: allProducts });
  const [clientes, setClientes] = useState([]); // clientes já vistos nesta tela (o da mesa, os escolhidos e os criados)
  const guardarCliente = (c) => { if (c && c.id) setClientes((prev) => [...(prev || []).filter((x) => x.id !== c.id), c]); };
  // Resultado da busca do campo Cliente — {termo, lista} (servidor, até 8, sem foto)
  const [buscaCliente, setBuscaCliente] = useState({ termo: "", lista: [] });
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [showCadastrarCliente, setShowCadastrarCliente] = useState(false);
  const [showCadastrarForm, setShowCadastrarForm] = useState(false);
  const [novoClienteNome, setNovoClienteNome] = useState("");
  const [novoClienteTelefone, setNovoClienteTelefone] = useState("");
  const [cadastrandoCliente, setCadastrandoCliente] = useState(false);
  const [novoClienteCpf, setNovoClienteCpf] = useState("");
  const [novoClienteNascimento, setNovoClienteNascimento] = useState("");
  const [comandaVal, setComandaVal] = useState(""); // comanda: só números
  const [mesaInfoExpanded, setMesaInfoExpanded] = useState(false); // mobile: barra slim por padrão
  const [confirmEnvioOpen, setConfirmEnvioOpen] = useState(false); // dialog de conferência antes de enviar
  // Teclado (26/10/2026, features/mesas-teclado): mesa destacada pelas setas e
  // mesa cujo pagamento abre assim que a tela da mesa fechar (Espaço lá dentro)
  const [mesaFoco, setMesaFoco] = useState(null);
  const [pagarAoSair, setPagarAoSair] = useState(null);
  const [enviandoPedido, setEnviandoPedido] = useState(false); // trava anti duplo-envio (caso #93099: 3 cliques triplicaram o pedido)
  
  // Tema (para ProductPopupCardapio e cards) — lê localStorage e escuta themeChange
  const [isDark, setIsDark] = useState(() => typeof localStorage !== "undefined" && localStorage.getItem("darkMode") === "true");
  useEffect(() => {
    const onTheme = () => setIsDark(document.documentElement.classList.contains("dark"));
    window.addEventListener("themeChange", onTheme);
    return () => window.removeEventListener("themeChange", onTheme);
  }, []);
  
  // Popup de produto (ProductPopupCardapio: combo, etapas, etc.)
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Edição de item do carrinho (v1.5.469): guarda QUAL linha está sendo
  // editada e as escolhas dela (pra pré-preencher o popup). Ao confirmar, a
  // linha antiga é SUBSTITUÍDA; ao cancelar, nada muda.
  const [editingCartItemId, setEditingCartItemId] = useState(null);
  const [editingInitial, setEditingInitial] = useState(null);
  // Conferência (v1.5.513): itens DESMARCADOS pra impressão do cupom de
  // PREPARO. Guarda a exceção (desmarcados) de propósito — o padrão é todos
  // marcados, então abrir a conferência zera o conjunto.
  const [naoImprimirPreparo, setNaoImprimirPreparo] = useState(new Set());

  // Mobile: drawer do carrinho na tela de vendas
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  // Celular: a busca mora na lupa fixa da barra de categorias e abre no lugar dos chips
  const [buscaAberta, setBuscaAberta] = useState(false);
  const fecharBusca = () => { setProductSearchTerm(""); setBuscaAberta(false); };
  
  // Pagamento (Fechar mesa — formas do backend, igual Cardápio)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [cashReceived, setCashReceived] = useState("");
  // Modal de troco (v1.5.518): abre ao escolher DINHEIRO no Fechar Mesa
  const [showTrocoModal, setShowTrocoModal] = useState(false);
  const [paymentMethodsFromApi, setPaymentMethodsFromApi] = useState([]);
  // Pagamento dividido (mesmo padrão do Balcão)

  // Seleção de produtos no pagamento (divisão por produto — opcional)
  const [paymentItems, setPaymentItems] = useState([]); // [{...item, _paid: bool, _selected: bool, _paymentInfo: string}]
  // Mesa QR: produto lançado para uma pessoa e cobrança/saída por pessoa
  const donos = useDonosCarrinho(selectedMesa, mesaVendasOpen, setCart);
  const [lendoSaida, setLendoSaida] = useState(false);
  const saidaQrLigada = useSaidaQrLigada();
  // Renomear a mesa pergunta se o nome no meio do QR Code muda junto
  const { perguntar: perguntarNomeQr, dialogo: dialogoNomeQr } = usePerguntaNomeQr();
  const [showProductSelect, setShowProductSelect] = useState(true); // painel de produtos sempre visível no Fechar mesa

  // Desconto aplicado no fechamento — espelhando exatamente o fluxo do Delivery > Novo Pedido
  const [discountType, setDiscountType] = useState('none'); // 'none' | 'percent' | 'fixed' | 'coupon'
  const [discountValue, setDiscountValue] = useState(0);
  // Override manual do Total a pagar no modal Fechar mesa — string ('' = nao editado, usa o calculado)
  const [totalOverride, setTotalOverride] = useState('');
  // Acumulador de pagamentos parciais feitos via override (sem selecionar items)
  const [partialPaid, setPartialPaid] = useState(0);
  // Log de pagamentos realizados nesta sessao de fechamento — usado pra imprimir no cupom
  const [paymentLog, setPaymentLog] = useState([]);
  // Produto cancelado (rodada do QR) sai da mesa do Fechar mesa: é dela que
  // vem o Total a pagar
  const removerDaMesaAberta = useCallback((ids) => {
    setSelectedMesa((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((i) => (i.cartItemId != null && ids.has(String(i.cartItemId)) ? { ...i, deleted: true } : i));
      return { ...prev, items, total: recalcularTotalMesa({ items }) };
    });
  }, []);
  const cobranca = useCobrancaPorPessoa(selectedMesa, paymentDialogOpen,
    { itens: paymentItems, setItens: setPaymentItems, setLog: setPaymentLog, onRemover: removerDaMesaAberta });
  const [couponData, setCouponData] = useState(null);
  // Divisão da conta em partes iguais (features/mesas-fechamento). Fica
  // gravada em mesa.pagamentos junto com os parciais — sobrevive a fechar o popup.
  const [divisao, setDivisao] = useState(null);
  // Desconto que já entrou em partes pagas de divisões (vai pro pedido no fechamento)
  const [descontoAcumulado, setDescontoAcumulado] = useState(null);

  // PIX Online
  const [showPixModal, setShowPixModal] = useState(false);
  const [showPixConfirm, setShowPixConfirm] = useState(false); // popup "gerar QR?" antes da cobranca
  const [pixOrderId, setPixOrderId] = useState(null);
  const [pixValor, setPixValor] = useState(0); // valor da cobrança PIX (o que falta pagar no momento)

  // Carregar mesas do SQL (sincronizado entre aparelhos)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mesas`, getAuthHeader());
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        if (list.length === 0) {
          const initial = Array.from({ length: 10 }, (_, i) => ({
            id: `mesa-${i + 1}`,
            numero: i + 1,
            nome: `Mesa ${i + 1}`,
            status: "fechada",
            items: [],
            total: 0,
            cliente: "",
            comanda: null
          }));
          await axios.put(`${API}/mesas`, { mesas: initial }, getAuthHeader());
          if (!cancelled) setMesas(initial);
        } else {
          // Recalcular total de cada mesa excluindo itens deletados
          const listRecalculada = list.map((m) => ({
            ...m,
            total: recalcularTotalMesa(m)
          }));
          setMesas(listRecalculada);
        }
      } catch (e) {
        if (cancelled) return;
        const saved = localStorage.getItem("mesas");
        if (saved) {
          try {
            setMesas(JSON.parse(saved));
            toast.info("Mesas carregadas do dispositivo. Conecte para sincronizar.");
          } catch (_) {}
        } else {
          setMesas([]);
          toast.error("Erro ao carregar mesas.");
        }
      } finally {
        if (!cancelled) setPrimeiraCarga(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refresh das mesas a cada 5s (para ver se foram abertas/fechadas em outro aparelho)
  // Pausar durante edição para não sobrescrever alterações do operador
  const isEditingRef = useRef(false);
  useEffect(() => {
    isEditingRef.current = mesaVendasOpen || paymentDialogOpen || transferDialogOpen;
  }, [mesaVendasOpen, paymentDialogOpen, transferDialogOpen]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (isEditingRef.current) return; // Pausar polling durante edição
      try {
        const { data } = await axios.get(`${API}/mesas`, getAuthHeader());
        const list = Array.isArray(data) ? data : [];
        // Recalcular total de cada mesa excluindo itens deletados
        const listRecalculada = list.map((m) => ({
          ...m,
          total: recalcularTotalMesa(m)
        }));
        setMesas(listRecalculada);
        setSelectedMesa((prev) => {
          if (!prev) return null;
          const found = listRecalculada.find((m) => m.id === prev.id);
          return found || prev;
        });
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Salvar mesas no SQL (qualquer aparelho pode ver e editar)
  // Devolve se gravou (a lixeira do Fechar mesa só muda a tela depois de gravar)
  const saveMesas = async (newMesas) => {
    try {
      await axios.put(`${API}/mesas`, { mesas: newMesas }, getAuthHeader());
      // Campos só do fechamento não ficam na lista: senão o próximo save de
      // OUTRA mesa reenviaria este fechamento (tela parada = polling pausado)
      setMesas(newMesas.map(({ fechamento_pago: _fp, sessao_fechada: _sf, ...m }) => m));
      return true;
    } catch (e) {
      // O servidor explica quando recusa de propósito ("Abra o caixa para receber…")
      const detalhe = e?.response?.data?.detail;
      toast.error(typeof detalhe === "string" && detalhe ? detalhe : "Erro ao salvar mesas. Tente novamente.", { duration: 8000 });
      return false;
    }
  };

  // Formas de pagamento do backend (módulo mesas)
  useEffect(() => {
    if (!mesaVendasOpen && !paymentDialogOpen) return;
    let ok = true;
    axios.get(`${API}/payment-methods/module/mesas`, getAuthHeader())
      .then((res) => {
        if (!ok || !Array.isArray(res.data)) return;
        // v1.5.517: o mapa antigo casava o code por IGUALDADE ("dinheiro"),
        // mas os codes reais vêm com o tenant no sufixo ("dinheiro_<uuid>") —
        // nada casava e TODAS as formas caíam no cinza genérico (print do
        // dono). pmVisual casa por substring no code E no nome.
        setPaymentMethodsFromApi(res.data.map((pm) => {
          const vis = pmVisual(pm.code, pm.name);
          return {
            id: pm.code || pm.id,
            code: pm.code,
            label: pm.name || pm.code,
            icon: vis.icon,
            color: vis.color,
          };
        }));
      })
      .catch(() => { if (ok) setPaymentMethodsFromApi([]); });
    return () => { ok = false; };
  }, [mesaVendasOpen, paymentDialogOpen]);

  // Filtrar mesas
  const filteredMesas = mesas.filter(mesa => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      String(mesa.numero).includes(search) ||
      (mesa.comanda && String(mesa.comanda).includes(search)) ||
      (mesa.cliente && mesa.cliente.toLowerCase().includes(search)) ||
      (mesa.nome && mesa.nome.toLowerCase().includes(search))
    );
  });

  // Separar fechamento, abertas e fechadas
  const mesasFechamento = filteredMesas.filter(m => m.status === "fechamento").sort((a, b) => a.numero - b.numero);
  const mesasAbertas = filteredMesas.filter(m => m.status === "aberta").sort((a, b) => a.numero - b.numero);
  const mesasFechadas = filteredMesas.filter(m => m.status === "fechada").sort((a, b) => a.numero - b.numero);

  // Handlers de Mesa
  const handleCreateMesa = () => {
    setEditMode(false);
    setCurrentMesa(null);
    setMesaNome("");
    setMesaNumero(String(mesas.length + 1));
    setMesaDialogOpen(true);
  };

  const handleEditMesa = (mesa) => {
    setEditMode(true);
    setCurrentMesa(mesa);
    setMesaNome(mesa.nome);
    setMesaNumero(String(mesa.numero));
    setMesaDialogOpen(true);
  };

  const handleSaveMesa = () => {
    if (!mesaNumero) {
      toast.error("Informe o número da mesa");
      return;
    }

    const numero = parseInt(mesaNumero);
    
    if (!editMode || (editMode && currentMesa.numero !== numero)) {
      if (mesas.some(m => m.numero === numero)) {
        toast.error("Já existe uma mesa com este número");
        return;
      }
    }

    if (editMode && currentMesa) {
      const updatedMesas = mesas.map(m =>
        m.id === currentMesa.id
          ? { ...m, nome: mesaNome || `Mesa ${numero}`, numero }
          : m
      );
      saveMesas(updatedMesas);
      toast.success("Mesa atualizada!");
      perguntarNomeQr(currentMesa, mesaNome || `Mesa ${numero}`);
    } else {
      const newMesa = {
        id: `mesa-${Date.now()}`,
        numero,
        nome: mesaNome || `Mesa ${numero}`,
        status: "fechada",
        items: [],
        total: 0,
        cliente: "",
        comanda: null
      };
      saveMesas([...mesas, newMesa]);
      toast.success("Mesa criada!");
    }

    setMesaDialogOpen(false);
  };

  const handleDeleteMesa = (mesa) => {
    setMesaToDelete(mesa);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (mesaToDelete) {
      saveMesas(mesas.filter(m => m.id !== mesaToDelete.id));
      toast.success("Mesa deletada!");
    }
    setDeleteDialogOpen(false);
    setMesaToDelete(null);
  };

  const handleAbrirCancelarMesa = (mesa, motivo = "") => {
    setMesaParaCancelar(mesa);
    setMotivoCancelamentoMesa(motivo);
    setCancelarMesaModalOpen(true);
  };
  // "Liberar mesa" do cartão com tudo cancelado: o mesmo Cancelar Mesa, com o motivo pronto
  const handleLiberarMesa = (mesa) => handleAbrirCancelarMesa(mesa, "Todos os produtos da mesa foram cancelados");

  const handleCancelarMesa = async () => {
    if (!mesaParaCancelar) return;
    if (!motivoValido(motivoCancelamentoMesa)) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }
    try {
      const { data: pedidosMesa } = await axios.get(`${API}/pedidos/by-mesa/${mesaParaCancelar.id}`, getAuthHeader());
      for (const p of pedidosMesa || []) {
        await axios.patch(`${API}/pedidos/${p.id}/cancelar`, { motivo: motivoCancelamentoMesa.trim() }, getAuthHeader());
      }
      const updatedMesa = {
        ...mesaParaCancelar,
        pagamentos: null,
        status: "fechada",
        items: [],
        total: 0,
        cliente: "",
        cliente_id: null,
        comanda: null,
        codigo: null,
        pedido_id: null,
        pessoas: 0,
        opened_at: mesaParaCancelar.opened_at,
        closed_at: new Date().toISOString(),
        sessao_fechada: mesaParaCancelar.opened_at || null,
      };
      const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
      await saveMesas(updatedMesas);
      toast.success("Mesa cancelada. Os pedidos ficaram com status Cancelado em Pedidos.");
      setCancelarMesaModalOpen(false);
      setMesaParaCancelar(null);
      setMotivoCancelamentoMesa("");
    } catch (e) {
      toast.error("Erro ao cancelar mesa.");
    }
  };

  // Transferir mesa — abre dialog para escolher destino
  const handleAbrirTransferir = (mesa) => {
    setMesaOrigem(mesa);
    setMesaDestinoId("");
    setTransferDialogOpen(true);
  };

  const handleTransferirMesa = async () => {
    if (!mesaOrigem || !mesaDestinoId) return;
    const destino = mesas.find(m => m.id === mesaDestinoId);
    if (!destino) { toast.error("Mesa destino não encontrada."); return; }
    if (destino.status === "aberta" || destino.status === "fechamento") {
      toast.error("A mesa destino já está ocupada. Escolha uma mesa disponível.");
      return;
    }

    const updatedOrigem = {
      ...mesaOrigem,
      pagamentos: null,
      status: "fechada",
      items: [],
      total: 0,
      cliente: "",
      cliente_id: null,
      comanda: null,
      codigo: null,
      pedido_id: null,
      pessoas: 0,
      opened_at: null,
      closed_at: new Date().toISOString(),
      sessao_fechada: mesaOrigem.opened_at || null,
    };

    const updatedDestino = {
      ...destino,
      status: "aberta",
      items: mesaOrigem.items || [],
      total: mesaOrigem.total || 0,
      cliente: mesaOrigem.cliente || "",
      cliente_id: mesaOrigem.cliente_id || null,
      comanda: mesaOrigem.comanda,
      codigo: mesaOrigem.codigo,
      pedido_id: mesaOrigem.pedido_id || null,
      pessoas: mesaOrigem.pessoas || 0,
      opened_at: mesaOrigem.opened_at,
      closed_at: null,
      // Pagamentos parciais acompanham a transferência (v1.5.520)
      pagamentos: mesaOrigem.pagamentos || null,
    };

    // Atualizar pedido com nova mesa_id
    if (mesaOrigem.pedido_id) {
      try {
        await axios.put(`${API}/pedidos/${mesaOrigem.pedido_id}`, {
          mesa_id: destino.id,
          endereco_rua: `Mesa ${destino.numero}${updatedDestino.comanda ? ` - Comanda #${updatedDestino.comanda}` : ""}`,
        }, getAuthHeader());
      } catch (e) {
        console.error("[Mesas] transfer pedido error:", e);
      }
    }

    const updatedMesas = mesas.map((m) => {
      if (m.id === mesaOrigem.id) return updatedOrigem;
      if (m.id === destino.id) return updatedDestino;
      return m;
    });
    await saveMesas(updatedMesas);
    toast.success(`Pedido transferido da Mesa ${mesaOrigem.numero} → Mesa ${destino.numero}`);
    setTransferDialogOpen(false);
    setMesaOrigem(null);
    setMesaDestinoId("");
  };

  // Reabrir mesa (de fechamento → aberta)
  const handleReabrirMesa = async (mesa) => {
    const updatedMesa = { ...mesa, status: "aberta" };
    const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
    await saveMesas(updatedMesas);
    toast.success(`Mesa ${mesa.numero} reaberta.`);
  };

  const fetchPedidosMesa = async (mesaId, openedAt = null) => {
    let url = `${API}/pedidos/by-mesa/${mesaId}?incluir_concluidos=true`;
    if (openedAt) {
      url += `&opened_at=${encodeURIComponent(openedAt)}`;
    }
    const { data } = await axios.get(url, getAuthHeader());
    return Array.isArray(data) ? data : [];
  };

  const buildVirtualPedidoPreparo = (mesa, pedidos) => {
    // Preferir mesa.items (local storage) que contém subitems/etapas
    // Fallback para pedidos API (que podem não ter subitems)
    let items;
    if (mesa.items && mesa.items.length > 0) {
      items = mesa.items.map((i) => ({
        // id do produto: é por ele que o cupom respeita o "imprimir preparo"
        // do produto (Produtos → Impressão) — sem ele, tudo imprimia (achado 328)
        id: i.id ?? i.product_id ?? null,
        nome: i.name || i.nome,
        quantidade: i.quantity || i.quantidade || 1,
        combo_type: i.combo_type || null,
        observacao: i.observation || i.observacao || null,
        subitems: i.subitems || undefined,
        etapas: i.etapas || undefined,
        deleted: i.deleted || false,
      }));
    } else {
      items = (pedidos || []).flatMap((p) => p.items || []);
    }
    return {
      items,
      cliente_nome: mesa.cliente || "—",
      codigo: (mesa.codigo != null && String(mesa.codigo).trim() !== "")
        ? String(mesa.codigo).trim()
        : "#" + String(mesa.numero).padStart(5, "0"),
      created_at: (pedidos || [])[0]?.created_at || new Date().toISOString(),
      mesa_numero: mesa.numero,
      mesa_nome: mesa.nome || null,  // apelido completo p/ extracao do sufixo no cupom (ex: 'Mesa 1A' -> '1A')
      comanda: (mesa.comanda != null && String(mesa.comanda).trim() !== "") ? String(mesa.comanda).trim() : null,
      modulo: "Mesas",
    };
  };

  const getProductPrice = (pid) => (allProducts || []).find((p) => p.id === pid)?.sale_price || 0;
  const getItemPrice = (item, step) => {
    if (step?.sync_prices !== false) return getProductPrice(item.product_id);
    if (item.price_override === 0) return 0;
    if (item.price_override > 0) return item.price_override;
    return getProductPrice(item.product_id);
  };

  // Converte itens do carrinho (novos itens) para o formato do cupom de preparo (nome, quantidade, subitems, etc.)
  const cartToPreparoItems = (cart, allProducts) => {
    return (cart || []).map((i) => {
      const subitems = [];
      const steps = i.order_steps || [];
      const sel = i.step_selections || {};
      for (let idx = 0; idx < steps.length; idx++) {
        const step = steps[idx];
        const ids = sel[idx] ?? sel[step?.id] ?? sel[step?.name] ?? [];
        const arr = Array.isArray(ids) ? ids : (ids != null ? [ids] : []);
        for (const id of arr) {
          const it = (step?.items || []).find((x) => x.product_id === id || String(x?.product_id) === String(id));
          const nm = it?.product_name || it?.name;
          if (nm) subitems.push({ step_name: step?.name, nome: nm });
          else {
            const p = (allProducts || []).find((x) => x.id === id || String(x?.id) === String(id));
            if (p) subitems.push({ step_name: step?.name, nome: p.name });
          }
        }
      }
      // Buscar ticket_route do produto (configurado em Produtos > Impressão)
      const prod = (allProducts || []).find(p => p.id === i.id || p.id === i.product_id);
      return {
        id: i.id ?? i.product_id ?? null, // respeitar o preparo do produto (achado 328)
        nome: i.name || i.nome,
        quantidade: i.quantity || 1,
        combo_type: i.combo_type || null,
        observacao: i.observation || null,
        // Pizza não tem etapas: os sabores e a borda vêm prontos do popup — sem
        // o recuo o cupom de preparo saía sem os sabores (dono, 25/10/2026)
        subitems: subitemsComPizza(i, subitems).length ? subitemsComPizza(i, subitems) : undefined,
        etapas: undefined,
        ticket_route: prod?.print_config?.ticket_route || i.ticket_route || "COZINHA",
      };
    });
  };

  const handleImprimirAmbos = async (mesa) => {
    try {
      const pedidos = await fetchPedidosMesa(mesa.id, mesa.opened_at);
      const virtual = buildVirtualPedidoPreparo(mesa, pedidos);
      await Promise.all([imprimirCupomFechamento(mesa, pedidos), imprimirCupomPreparo(virtual)]);
    } catch (err) {
      toast.error(err?.message || "Erro ao imprimir");
    }
  };

  const handleImprimirFechamento = async (mesa) => {
    try {
      const pedidos = await fetchPedidosMesa(mesa.id, mesa.opened_at);
      await imprimirCupomFechamento(mesa, pedidos);
    } catch (err) {
      toast.error(err?.message || "Erro ao imprimir");
    }
  };

  const handleImprimirPreparo = async (mesa) => {
    try {
      const pedidos = await fetchPedidosMesa(mesa.id, mesa.opened_at);
      const virtual = buildVirtualPedidoPreparo(mesa, pedidos);
      await imprimirCupomPreparo(virtual);
    } catch (err) {
      toast.error(err?.message || "Erro ao imprimir");
    }
  };

  // Cardápio da tela de lançar (produtos, categorias, promoções). Pré-carregado
  // ao abrir a aba e reaproveitado: ADICIONAR ITENS abre na hora com o que já
  // tem e atualiza por trás (dono, 25/09/2026 — no celular a espera incomodava).
  const cardapioCarregadoEm = useRef(0);
  // A atualização por trás só redesenha a grade quando algo mudou (25/09/2026:
  // no celular lento, redesenhar o cardápio inteiro a cada toque pesava)
  const ultimoCardapio = useRef({});
  const carregarCardapio = useCallback(async () => {
    const seMudou = (chave, dados, setter) => {
      const txt = JSON.stringify(dados);
      if (ultimoCardapio.current[chave] === txt) return;
      ultimoCardapio.current[chave] = txt;
      setter(dados);
    };
    const [productsRes, categoriesRes, allProductsRes, promosRes] = await Promise.all([
      axios.get(`${API}/products/for-sale?module=Mesas`, getAuthHeader()),
      axios.get(`${API}/categories`, getAuthHeader()),
      axios.get(`${API}/products`, getAuthHeader()),
      axios.get(`${API}/promotions/active?module=Mesas`).catch(() => ({ data: [] })),
    ]);
    seMudou("produtos", productsRes.data, setProducts);
    seMudou("categorias", categoriesRes.data, setCategories);
    seMudou("todos", allProductsRes.data || [], setAllProducts);
    seMudou("promocoes", Array.isArray(promosRes.data) ? promosRes.data : [], setActivePromotions);
    cardapioCarregadoEm.current = Date.now();
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { carregarCardapio().catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [carregarCardapio]);

  // Tela de Vendas
  const handleMesaClick = async (mesa) => {
    setSelectedMesa(mesa);
    fecharBusca();
    setCart([]);  // cart = apenas itens novos (ainda não enviados); mesa.items = enviados anteriormente
    setClienteNome(mesa.cliente || "");
    setSelectedClienteId(mesa.cliente_id || null);
    setShowCadastrarCliente(false);
    setShowCadastrarForm(false);
    setNovoClienteNome("");
    setNovoClienteTelefone("");
    setComandaVal(mesa.comanda != null && String(mesa.comanda).trim() !== "" ? String(mesa.comanda).replace(/\D/g, "") : "");
    setPessoasVal(mesa.pessoas ? String(mesa.pessoas) : "");
    setMesaObservacao(mesa.observacao || "");
    // Mesa nova (livre virando aberta) — expande o form pra preencher.
    // Mesa ja aberta (sendo editada) — recolhe pra dar espaco aos produtos.
    setMesaInfoExpanded(mesa.status !== 'aberta');
    setMesaVendasOpen(true);
    
    // Com o cardápio já em mãos a tela abre na hora; a atualização vem por trás
    const jaTem = cardapioCarregadoEm.current > 0;
    if (!jaTem) setLoading(true);
    carregarCardapio()
      .catch(() => { if (!jaTem) toast.error("Erro ao carregar dados"); })
      .finally(() => setLoading(false));
    // Só o cliente da mesa (telefone/e-mail vão no pedido). A lista inteira —
    // com a foto do WhatsApp de cada um, MBs em loja grande e o servidor
    // recalculando a base — era baixada a cada toque (25/09/2026); o campo
    // Cliente agora busca no servidor enquanto digita.
    if (mesa.cliente_id) {
      axios.get(`${API}/clientes/${mesa.cliente_id}`, getAuthHeader())
        .then(({ data }) => guardarCliente(data))
        .catch(() => {});
    }
  };

  // "Ir até ela" do Fechar caixa (features/caixa/fechamento/AvisoEmAberto):
  // /mesas?mesa=<id> abre a mesa assim que a lista chega e limpa o parâmetro.
  // A função de abrir muda a cada render, por isso vai por ref (sem ela o
  // efeito rodaria de novo a cada render ou exigiria desligar o lint).
  const [searchParams, setSearchParams] = useSearchParams();
  const mesaDoLink = searchParams.get("mesa");
  const abrirMesaRef = useRef(handleMesaClick);
  abrirMesaRef.current = handleMesaClick;
  useEffect(() => {
    if (!mesaDoLink || mesas.length === 0) return;
    const alvo = mesas.find((m) => m.id === mesaDoLink);
    const next = new URLSearchParams(searchParams);
    next.delete("mesa");
    setSearchParams(next, { replace: true });
    if (alvo) abrirMesaRef.current(alvo);
    else toast.error("Não achei essa mesa — ela pode ter sido apagada.");
  }, [mesaDoLink, mesas, searchParams, setSearchParams]);

  // Voltar da tela de lançar (dono, 25/09/2026): grava cliente, comanda,
  // pessoas e observação que mudaram — antes só o ENVIAR gravava, e ele exige
  // produto novo. Com produto no carrinho não enviado, pergunta antes de sair.
  const salvarInfoAoVoltar = async () => {
    if (!selectedMesa) return true;
    // A lista é a versão mais nova da mesa (rodada do QR que chegou com a tela aberta)
    const base = mesas.find((m) => m.id === selectedMesa.id) || selectedMesa;
    if (!guardaInfo(base)) return true;
    const info = infoEditada({
      mesa: base, clienteNome, clienteId: selectedClienteId,
      comandaVal, pessoasVal, observacao: mesaObservacao,
    });
    if (!infoMudou(base, info)) return true;
    if (comandaEmUso(mesas, base.id, info.comanda)) {
      toast.error("Já existe uma mesa aberta com esta comanda. Use outra comanda.");
      return false;
    }
    const atualizada = { ...base, ...info };
    const ok = await saveMesas(mesas.map((m) => (m.id === atualizada.id ? atualizada : m)));
    if (ok && base.pedido_id && obsDoPedidoMudou(base, info)) {
      // A observação vai junto pro pedido da mesa: é ela que o KDS mostra
      try {
        await axios.put(`${API}/pedidos/${base.pedido_id}`, { observacao: observacaoDoPedido(info.comanda, info.observacao) }, getAuthHeader());
      } catch (e) { console.error("[Mesas] observação do pedido:", e); }
    }
    if (ok) toast.success("Informações da mesa salvas");
    return ok;
  };

  const voltarDaMesa = async () => {
    if (cart.length > 0) {
      setSairSemEnviarOpen(true);
      return;
    }
    if (await salvarInfoAoVoltar()) closeMesaVendas();
  };

  const closeMesaVendas = () => {
    setMesaVendasOpen(false);
    setSelectedMesa(null);
    setCart([]);
    setClienteNome("");
    setSelectedClienteId(null);
    setShowClienteDropdown(false);
    setShowCadastrarCliente(false);
    setShowCadastrarForm(false);
    setNovoClienteNome("");
    setNovoClienteTelefone("");
    setComandaVal("");
    setMesaObservacao("");
    setSelectedCategory("all");
    setShowMobileCart(false);
    setProductSearchTerm("");
  };

  // Regras de categoria iguais ao Cardápio: product.category || "Outros"
  const productsByCategory = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      const cat = p.category || "Outros";
      if (!m[cat]) m[cat] = [];
      m[cat].push(p);
    });
    return m;
  }, [products]);

  // Categorias que têm produtos (ordem do backend) — igual Cardápio
  const categoriesWithProducts = useMemo(() => {
    const categorySet = new Set(products.map((p) => p.category).filter(Boolean));
    return categories.filter((cat) => categorySet.has(cat.name));
  }, [categories, products]);

  // Seções a exibir: "Todos" = categorias com produtos primeiro, depois "Outros" etc.
  const todasAsSecoes = useMemo(() => {
    const ordered = categoriesWithProducts
      .filter((cat) => productsByCategory[cat.name]?.length)
      .map((cat) => [cat.name, productsByCategory[cat.name]]);
    const others = Object.entries(productsByCategory).filter(
      ([n]) => !categoriesWithProducts.some((c) => c.name === n)
    );
    return [...ordered, ...others];
  }, [productsByCategory, categoriesWithProducts]);
  const sectionsToShow = useMemo(() => {
    if (selectedCategory === "all" || !selectedCategory) return todasAsSecoes;
    return [[selectedCategory, productsByCategory[selectedCategory] || []]];
  }, [selectedCategory, productsByCategory, todasAsSecoes]);
  const buscando = !!(productSearchTerm && productSearchTerm.trim());

  // Filtro por busca escrita (nome/descrição do produto) — procura no cardápio
  // inteiro, não só na categoria marcada (no celular os chips somem enquanto busca)
  const sectionsToShowFiltered = useMemo(() => {
    if (!buscando) return sectionsToShow;
    const q = productSearchTerm.toLowerCase().trim();
    // Código de barras digitado (parte ou inteiro) também acha o produto (30/09/2026)
    const peloCodigo = casaPeloCodigo(q, todasAsSecoes.flatMap(([, prods]) => prods));
    return todasAsSecoes
      .map(([cat, prods]) => [cat, prods.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        peloCodigo(p)
      )])
      .filter(([, prods]) => prods.length > 0);
  }, [sectionsToShow, todasAsSecoes, buscando, productSearchTerm]);

  // Pizza tem fluxo dedicado (tamanho → sabores → borda → adicionais → finalizar)
  const pizza = usePizzaPopup({
    clienteId: ofertasCliente.estado === "ativo" ? selectedClienteId : null,
    moduloOferta: "Mesas",
    onAddToCart: (item) => handleAddToCartFromPopup({
      ...item,
      final_price: item.preco || item.sale_price || 0,
      sale_price: item.preco || item.sale_price || 0,
      quantity: item.quantity || 1,
    }),
    allProducts,
    darkMode: isDark,
  });

  const openProductPopup = (product) => {
    if (pizza.tryOpenPizza(product)) return;
    setSelectedProduct(product);
    setProductPopupOpen(true);
  };

  const closeProductPopup = () => {
    setProductPopupOpen(false);
    setSelectedProduct(null);
    setEditingCartItemId(null);
    setEditingInitial(null);
  };

  // Editar item do carrinho: reabre o popup do produto ORIGINAL com as
  // escolhas do item. Pizza usa o popup próprio (sem pré-preencher — limitação
  // conhecida), mas a confirmação também SUBSTITUI a linha antiga.
  const handleEditCartItem = (item) => {
    const produto = allProducts.find((p) => p.id === item.id);
    if (!produto) {
      toast.error("Produto não encontrado no catálogo — remova e adicione de novo.");
      return;
    }
    setEditingCartItemId(item.cartItemId || item.id);
    if (pizza.tryOpenPizza(produto)) return;
    setEditingInitial({
      quantity: item.quantity,
      observation: item.observation,
      combo_type: item.combo_type,
      step_selections: item.step_selections,
    });
    setSelectedProduct(produto);
    setProductPopupOpen(true);
  };

  // Recebe o objeto do ProductPopupCardapio (combo_type, step_selections, final_price, order_steps, etc.)
  const handleAddToCartFromPopup = (data) => {
    // Item personalizado NUNCA agrupa. `subitems`/`etapas` entram na conta
    // porque a PIZZA guarda tamanho, sabores e borda ali — e em lugar nenhum
    // mais: ela nao seta observation/combo_type/step_selections. Sem isso,
    // uma Grande e uma Pequena do mesmo sabor viravam a MESMA linha (o `id`
    // da pizza e' o do sabor primario, nao o da combinacao) e a segunda sumia
    // com preco, tamanho e borda. Mesma regra que o PDV (Delivery.js) ja
    // aplicava corretamente.
    const hasCustom = temPersonalizacao(data);
    const unitPrice = data.final_price ?? data.sale_price ?? 0;
    const toAdd = donos.marcar({
      ...data,
      sale_price: unitPrice,
      cartItemId: Date.now(),
    });
    // Confirmação de uma EDIÇÃO: troca a linha antiga pela nova, na mesma
    // posição — sem passar pelo agrupamento (editar não é adicionar).
    if (editingCartItemId) {
      setCart(cart.map((i) =>
        (i.cartItemId || i.id) === editingCartItemId
          ? { ...toAdd, _cliente_id: i._cliente_id, _cliente_nome: i._cliente_nome }
          : i
      ));
      closeProductPopup();
      return;
    }
    fecharBusca();  // dono, 25/09/2026: adicionou, a busca volta limpa
    if (hasCustom) {
      setCart([...cart, toAdd]);
    } else {
      const ex = cart.find((i) => i.id === data.id && !temPersonalizacao(i) && donos.mesmoDono(i, toAdd));
      if (ex) {
        setCart(cart.map((i) => (i.cartItemId === ex.cartItemId ? { ...i, quantity: i.quantity + data.quantity } : i)));
      } else {
        setCart([...cart, toAdd]);
      }
    }
    closeProductPopup();
  };

  const updateQuantity = (cartItemId, delta) => {
    setCart(cart.map(item => {
      if ((item.cartItemId || item.id) === cartItemId) {
        const stepDelta = item.is_divisible ? (item.sale_unit === "kg" || item.sale_unit === "L" ? 0.1 : 0.5) : 1;
        const adjustedDelta = delta > 0 ? stepDelta : -stepDelta;
        const newQuantity = parseFloat((item.quantity + adjustedDelta).toFixed(3));
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => (item.cartItemId || item.id) !== cartItemId));
  };

  // Marca um item já enviado como deletado (não remove, apenas marca para aparecer rasurado)
  // `quantidade` (dono, 26/09/2026): cancelar só parte da linha — 2 Cocas, cancela 1
  const toggleItemDeleted = async (itemIdx, quantidade = null) => {
    if (!selectedMesa) return;
    // Produto pago (inteiro ou em parte) não sai da comanda (dono, 24/09/2026):
    // o dinheiro fica no caixa e o produto sumiria da conta. Primeiro apaga o
    // pagamento na lixeira do Fechar mesa.
    const alvoAntes = (selectedMesa.items || [])[itemIdx];
    if (alvoAntes && !alvoAntes.deleted) {
      const visiveis = (selectedMesa.items || []).filter(i => !i.deleted);
      if (pagoDoItem(contaDaMesa(selectedMesa), alvoAntes, visiveis.indexOf(alvoAntes))) {
        toast.error("Esse produto já foi pago. Para tirar, apague o pagamento no Fechar mesa antes.");
        return;
      }
    }
    // `deleted_at`: a cozinha mostra o cancelado riscado e, se o envio inteiro
    // foi cancelado, o cartão fica 10 min como "não fazer" (dono, 25/09/2026)
    const agora = new Date().toISOString();
    // Parte da linha: a original fica com o resto e a cópia cancelada vai pro fim
    const partes = quantidade ? dividirParaCancelar(alvoAntes, quantidade, agora, Date.now(), allProducts) : null;
    const updatedItems = (selectedMesa.items || []).map((item, idx) => {
      if (idx === itemIdx) {
        if (partes) return partes[0];
        return { ...item, deleted: !item.deleted, deleted_at: item.deleted ? null : agora };
      }
      return item;
    });
    if (partes) updatedItems.push(partes[1]);
    // Usa a função centralizada para recalcular o total
    const updatedMesa = { ...selectedMesa, items: updatedItems };
    updatedMesa.total = recalcularTotalMesa(updatedMesa);
    setSelectedMesa(updatedMesa);
    const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
    setMesas(updatedMesas);
    await saveMesas(updatedMesas);
    // Sincronizar o pedido DO ITEM (itens e total só dele). Rodada do QR tem
    // pedido próprio: riscar item dela atualiza aquele pedido; o do garçom
    // atualiza o pedido da mesa sem as rodadas do QR (antes ia a mesa inteira,
    // com o total da mesa inteira, para dentro do pedido do garçom).
    const alvo = updatedItems[itemIdx];
    const pedidoDoItem = (i) => (i._qid && i._pedido_id ? i._pedido_id : updatedMesa.pedido_id);
    const pedidoId = pedidoDoItem(alvo);
    if (pedidoId) {
      try {
        const doPedido = updatedItems.filter(i => pedidoDoItem(i) === pedidoId);
        const total = recalcularTotalMesa({ items: doPedido });
        if (pedidoId !== updatedMesa.pedido_id) {
          // Rodada do QR: marca só o item no pedido dela (mesma ordem da rodada)
          const pos = doPedido.indexOf(alvo);
          const { data: ped } = await axios.get(`${API}/pedidos/${pedidoId}`, getAuthHeader());
          const comQtd = (it, q) => ({ ...it, quantidade: q, ...(it.quantity != null ? { quantity: q } : {}) });
          const itensPedido = (ped?.items || []).map((it, k) => {
            if (k !== pos) return it;
            return partes ? comQtd(it, partes[0].quantity) : { ...it, deleted: !!alvo.deleted, deleted_at: alvo.deleted_at || null };
          });
          // A parte cancelada vai pro fim do pedido da rodada, igual na mesa
          if (partes && (ped?.items || [])[pos]) itensPedido.push({ ...comQtd(ped.items[pos], quantidade), deleted: true, deleted_at: agora });
          // O cashback usado no PIX do celular já abateu o total: mantém o desconto
          const liquido = Math.max(0, total - Number(ped?.cashback_usado || 0));
          await axios.put(`${API}/pedidos/${pedidoId}`, { items: itensPedido, total: liquido }, getAuthHeader());
        } else {
          const pedidoItems = doPedido.map(i => ({
            // id do produto: sem ele o pedido perdia quilo/litro, preparo e relatórios
            ...idDoProduto(i),
            nome: i.name || i.nome,
            quantidade: i.quantity || 1,
            preco: i.final_price ?? i.sale_price ?? 0,
            observacao: i.observation || i.observacao || null,
            photo_url: i.photo_url || null,
            combo_type: i.combo_type || null,
            ...(i.pesado_balanca ? { pesado_balanca: true } : {}),
            deleted: i.deleted || false,
            deleted_at: i.deleted_at || undefined,
            subitems: i.subitems || undefined,
            // Sem o dono, riscar um item apagava de quem era cada produto do pedido
            ...donos.noPedido(i),
            // Sem a hora do envio, riscar um item juntava de novo as comandas
            // da mesa no KDS.
            envio: i._envio || undefined,
          }));
          await axios.put(`${API}/pedidos/${pedidoId}`, { items: pedidoItems, total }, getAuthHeader());
        }
      } catch (e) { console.error("[Mesas] toggleItemDeleted error:", e); toast.error("Erro ao atualizar item no pedido"); return; }
    }
    toast.success(partes
      ? `${quantidade} ${quantidade === 1 ? "unidade cancelada" : "unidades canceladas"}`
      : (updatedItems[itemIdx].deleted ? "Item marcado como deletado" : "Item restaurado"));
  };

  const getPromoForProduct = (productId, comboType) => {
    for (const promo of activePromotions) {
      const pp = (promo.products || []).find(p => p.product_id === productId);
      if (pp) {
        if (comboType === 'combo' && pp.discount_value_combo) return { promo, value: pp.discount_value_combo, type: promo.discount_type };
        // Pizza por tamanho — sem valor único pra exibir; popup mostra preço promo por tamanho.
        if (pp.sizes && typeof pp.sizes === 'object' && pp.discount_value == null) return null;
        return { promo, value: pp.discount_value, type: promo.discount_type };
      }
    }
    return null;
  };
  const calcPromoPrice = (originalPrice, promoInfo) => {
    if (!promoInfo) return null;
    if (promoInfo.type === 'percentage') return originalPrice * (1 - promoInfo.value / 100);
    if (promoInfo.type === 'fixed_price') return promoInfo.value;
    return null;
  };

  const cartTotal = cart.reduce((total, item) => total + (item.final_price ?? item.sale_price ?? 0) * item.quantity, 0);
  const cartItemsCount = contarItens(cart, allProducts);
  // Itens ja enviados pra mesa (filtra deletados) — usado no rodape para mostrar resumo
  const mesaItemsCount = Array.isArray(selectedMesa?.items) ? contarItens(selectedMesa.items, allProducts) : 0;
  const mesaItemsTotal = selectedMesa?.total || 0;

  const requestEnvio = () => {
    if (!selectedMesa) return;
    if (cart.length === 0) {
      toast.error("Adicione produtos antes de enviar.");
      return;
    }
    setNaoImprimirPreparo(new Set()); // padrão: tudo imprime
    // Sem cupom de preparo na loja a conferência (que é de impressão) não tem
    // o que decidir: envia direto (dono, 25/09/2026)
    if (preparoAtivo === false) {
      handleSaveMesaVendas();
      return;
    }
    setConfirmEnvioOpen(true);
  };

  // Enviar da Conferência (botão ou Enter). O dialog fica aberto (com botão
  // travado) até o envio concluir — fechar antes deixava o lojista reclicar
  // "Enviar" na tela de trás com o mesmo carrinho (caso #93099: pedido triplicado).
  const confirmarEnvio = async () => {
    if (enviandoPedido) return;
    await handleSaveMesaVendas();
    setConfirmEnvioOpen(false);
  };

  const handleSaveMesaVendas = async () => {
    if (enviandoPedido) return; // clique duplicado com envio em andamento
    setEnviandoPedido(true);
    try {
      await _saveMesaVendasInner();
    } finally {
      setEnviandoPedido(false);
    }
  };

  const _saveMesaVendasInner = async () => {
    if (!selectedMesa) return;

    const comandaFinal = comandaVal.trim() ? comandaVal : (selectedMesa.comanda && String(selectedMesa.comanda).trim() ? selectedMesa.comanda : null);
    if (comandaFinal && String(comandaFinal).trim()) {
      const outra = mesas.find(m => (m.status === "aberta" || m.status === "fechamento") && m.id !== selectedMesa.id && m.comanda != null && String(m.comanda).trim() === String(comandaFinal).trim());
      if (outra) {
        toast.error("Já existe uma mesa aberta com esta comanda. Use outra comanda.");
        return;
      }
    }
    // `codigo` será preenchido com o codigo gerado pelo backend após criar o pedido
    // (mesa.codigo é cache do retorno do backend para próximos envios). Se ainda não temos
    // pedido nem cache, fica vazio até o POST responder logo abaixo.
    let codigo = selectedMesa.codigo || "";
    // pedido_id em variável local (não só no state): a updatedMesa no final
    // desta função precisa dele NESTE ciclo — antes o valor ficava só no
    // setSelectedMesa (assíncrono) e a mesa era salva sem pedido_id.
    let novoPedidoId = selectedMesa.pedido_id || null;
    const envioEm = new Date().toISOString();
    const itemsEnviadosAgora = cart.map((i) => donos.carimbar(i, envioEm));
    // Enriquecer itens do carrinho com subitems construídos (para local storage)
    const itemsEnriquecidos = itemsEnviadosAgora.map((i) => {
      if (i.subitems && i.subitems.length) return i; // Já tem subitems
      const subitems = [];
      const allSteps = i.order_steps || [];
      // Filtrar steps por combo_type (simples não inclui combo_only)
      const relevantSteps = i.combo_type === 'simples' ? allSteps.filter(s => !s.combo_only) : allSteps;
      const sel = i.step_selections || {};
      for (let idx = 0; idx < relevantSteps.length; idx++) {
        const step = relevantSteps[idx];
        const ids = sel[idx] ?? sel[step?.id] ?? sel[step?.name] ?? [];
        const arr = Array.isArray(ids) ? ids : (ids != null ? [ids] : []);
        const inicioDaEtapa = subitems.length;
        for (const id of arr) {
          const it = (step?.items || []).find((x) => x.product_id === id || String(x?.product_id) === String(id));
          const nm = it?.product_name || it?.name;
          if (nm) subitems.push({ step_name: step?.name || "", nome: nm, preco: getItemPrice(it, step) });
          else {
            const p = (allProducts || []).find((x) => x.id === id || String(x?.id) === String(id));
            if (p) subitems.push({ step_name: step?.name || "", nome: p.name, preco: p.sale_price ?? p.price ?? 0 });
          }
        }
        // Soma, Máximo ou Subtração: o valor de cada linha segue o tipo da etapa
        // (a soma das linhas é o que a etapa acrescenta ou abate no item).
        linhasDaEtapa(step, subitems.slice(inicioDaEtapa).map((sub) => ({ preco: sub.preco, qtd: 1 })))
          .forEach((valor, k) => { subitems[inicioDaEtapa + k].preco = valor; });
      }
      return subitems.length ? { ...i, subitems } : i;
    });
    const itemsAcumulados = [...(selectedMesa.items || []), ...itemsEnriquecidos];
    // Usa a função centralizada para calcular o total (exclui deletados, inclui subitems)
    const totalAcumulado = recalcularTotalMesa({ items: itemsAcumulados });
    // Se a mesa estava fechada (sem itens anteriores), sempre usar data/hora atual
    // Se a mesa já estava aberta (tinha itens), manter o opened_at existente
    const isReopeningMesa = (selectedMesa.items || []).length === 0 || selectedMesa.status === "fechada";
    const opened_at = itemsAcumulados.length > 0
      ? (isReopeningMesa ? new Date().toISOString() : (selectedMesa.opened_at || new Date().toISOString()))
      : null;

    if (itemsEnviadosAgora.length > 0) {
      const selectedCliente = selectedClienteId ? clientes.find((c) => c.id === selectedClienteId) : null;
      // Preparar itens formatados para o backend
      const formattedItems = itemsEnviadosAgora.map((i) => {
        const subitems = [];
        const allSteps = i.order_steps || [];
        const relevantSteps = i.combo_type === 'simples' ? allSteps.filter(s => !s.combo_only) : allSteps;
        const sel = i.step_selections || {};
        for (let idx = 0; idx < relevantSteps.length; idx++) {
          const step = relevantSteps[idx];
          const ids = sel[idx] ?? sel[step?.id] ?? sel[step?.name] ?? [];
          const arr = Array.isArray(ids) ? ids : (ids != null ? [ids] : []);
          const inicioDaEtapa = subitems.length;
          for (const id of arr) {
            const it = (step?.items || []).find((x) => x.product_id === id || String(x?.product_id) === String(id));
            const nm = it?.product_name || it?.name;
            if (nm) subitems.push({ step_name: step?.name || "", nome: nm, preco: getItemPrice(it, step) });
            else {
              const p = (allProducts || []).find((x) => x.id === id || String(x?.id) === String(id));
              if (p) subitems.push({ step_name: step?.name || "", nome: p.name, preco: p.sale_price ?? p.price ?? 0 });
            }
          }
          // Soma, Máximo ou Subtração: o valor de cada linha segue o tipo da etapa
          // (a soma das linhas é o que a etapa acrescenta ou abate no item).
          linhasDaEtapa(step, subitems.slice(inicioDaEtapa).map((sub) => ({ preco: sub.preco, qtd: 1 })))
            .forEach((valor, k) => { subitems[inicioDaEtapa + k].preco = valor; });
        }
        const finalSubitems = subitems.length ? subitems : (i.subitems || []);
        return {
          // id do produto (30/09/2026): o item gravado não guarda a unidade de
          // venda — sem o id, o pedido da mesa não sabia que era quilo/litro
          ...idDoProduto(i),
          nome: i.name || i.nome,
          quantidade: i.quantity || 1,
          preco: i.final_price ?? i.sale_price ?? 0,
          observacao: i.observation || null,
          photo_url: i.photo_url || null,
          combo_type: i.combo_type || null,
          // Peso lido da balança ligada ao computador (features/balanca)
          ...(i.pesado_balanca ? { pesado_balanca: true } : {}),
          subitems: finalSubitems.length ? finalSubitems : undefined,
          // Pizza: tamanho, sabores e borda escolhidos — o servidor confere o
          // preço da rodada com a mesma conta do popup (24/10/2026)
          pizza_meta: pizzaMetaDoItem(i),
          ...donos.noPedido(i),
          // Cada envio vira uma comanda própria no KDS (features/kds/lib/envios.js)
          envio: envioEm,
        };
      });

      try {
        // Se a mesa já tem um pedido, APPEND items ao pedido existente
        if (selectedMesa.pedido_id) {
          const res = await axios.patch(`${API}/pedidos/${selectedMesa.pedido_id}/append-items`, {
            items: formattedItems,
            total: cartTotal,
          }, getAuthHeader());
          // Preserva o código real do backend (o backend mantém o código original do pedido)
          if (res.data?.codigo) codigo = res.data.codigo;
          // Observação/comanda mudaram nesta rodada: o pedido (e o KDS) recebe a nova
          if (obsDoPedidoMudou(selectedMesa, { comanda: comandaFinal, observacao: mesaObservacao })) {
            await axios.put(`${API}/pedidos/${selectedMesa.pedido_id}`,
              { observacao: observacaoDoPedido(comandaFinal, mesaObservacao) }, getAuthHeader()).catch(() => {});
          }
        } else {
          // Primeiro envio: criar pedido novo
          const res = await axios.post(`${API}/pedidos`, {
            cliente_id: selectedClienteId || null,
            cliente_nome: clienteNome || (selectedMesa.cliente || "Mesa"),
            cliente_telefone: selectedCliente?.telefone ?? null,
            cliente_email: selectedCliente?.email ?? null,
            items: formattedItems,
            total: cartTotal,
            status: "producao",
            tipo_entrega: "mesa",
            endereco_rua: `Mesa ${selectedMesa.numero}${comandaFinal ? ` - Comanda #${comandaFinal}` : ""}`,
            modulo: "Mesas",
            observacao: [comandaFinal ? `Comanda: ${comandaFinal}` : null, mesaObservacao?.trim() || null].filter(Boolean).join(" — ") || null,
            valor_entrega: 0,
            mesa_id: selectedMesa.id,
            mesa_opened_at: opened_at || selectedMesa.opened_at || null,
          }, getAuthHeader());
          // Guardar o pedido_id e o codigo real (gerado pelo backend) na mesa.
          // Em variável local: o setSelectedMesa é assíncrono e a updatedMesa
          // abaixo é montada neste mesmo ciclo — usar só o state deixava a mesa
          // salva SEM pedido_id, e o próximo envio criava um SEGUNDO pedido
          // (causa das mesas com 2 pedidos e venda no caixa com código trocado).
          if (res.data?.codigo) codigo = res.data.codigo;
          novoPedidoId = res.data?.id || null;
        }
      } catch (e) {
        // Com o id do produto no item, o servidor confere esgotado/preço no
        // 1º envio: mostra o motivo dele ("O produto X está esgotado...")
        const motivo = e?.response?.data?.detail;
        toast.error(typeof motivo === "string" && motivo ? motivo : "Erro ao registrar pedido. Tente novamente.");
        return;
      }
      // Carrinho zerado IMEDIATAMENTE após o backend confirmar — reclicar
      // "Enviar" a partir daqui não tem mais o que reenviar (caso #93099).
      setCart([]);
      setSelectedMesa(prev => prev ? ({
        ...prev,
        pedido_id: novoPedidoId,
        codigo: codigo || prev.codigo,
        items: itemsAcumulados,
        total: totalAcumulado,
      }) : prev);
      // Fallback: se por algum motivo o backend não retornou codigo, usa um local pra não imprimir vazio
      if (!codigo) codigo = generateMesaCodigo();
      window.dispatchEvent(new CustomEvent("pedidosUpdated"));

      // Cupom de preparo só dos novos itens — automático, sem abrir frame
      (async () => {
        try {
          // Respeita os checkboxes da conferência: item desmarcado não sai no
          // cupom de PREPARO (o pedido em si vai completo — só a impressão muda).
          const itensParaImprimir = itemsEnviadosAgora.filter(
            (i) => !naoImprimirPreparo.has(i.cartItemId || i.id)
          );
          if (itensParaImprimir.length === 0) return; // tudo desmarcado: sem cupom
          const itemsPreparo = cartToPreparoItems(itensParaImprimir, allProducts);
          const virtual = {
            items: itemsPreparo,
            codigo,
            created_at: new Date().toISOString(),
            cliente_nome: clienteNome || (selectedMesa.cliente || "Mesa"),
            mesa_numero: selectedMesa.numero,
            mesa_nome: selectedMesa.nome || null,  // pra cupom de preparo extrair apelido (1A, 11F)
            comanda: comandaFinal || null,
            modulo: "Mesas",
            _tituloItens: "— NOVOS ITENS —",
          };
          await imprimirCupomPreparo(virtual);
        } catch (e) {
          toast.error("Erro ao imprimir cupom de preparo: " + (e?.message || ""));
        }
      })();
    }

    const updatedMesa = {
      ...selectedMesa,
      status: itemsAcumulados.length > 0 ? "aberta" : "fechada",
      items: itemsAcumulados,
      total: totalAcumulado,
      cliente: clienteNome,
      cliente_id: selectedClienteId || null,
      comanda: comandaFinal,
      codigo,
      opened_at,
      pedido_id: novoPedidoId,
      pessoas: parseInt(pessoasVal) || 0,
      // v1.5.515: a observação era usada no texto do pedido mas NUNCA gravada
      // de volta na mesa — sumia ao reabrir (bug reportado pelo dono).
      observacao: mesaObservacao?.trim() || null,
      closed_at: itemsAcumulados.length > 0 ? null : new Date().toISOString(),
      sessao_fechada: itemsAcumulados.length > 0 ? undefined : (selectedMesa.opened_at || null),
    };

    const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
    await saveMesas(updatedMesas);

    toast.success("Pedido enviado!");
    closeMesaVendas();
  };

  // Total da mesa (enviados + novos) — usado ao Fechar mesa
  const totalMesa = (selectedMesa?.total || 0) + cartTotal;
  // O que já foi pago de cada produto enviado, para a comanda mostrar "pago" e
  // o "Falta pagar" com a mesa reaberta (dono, 24/09/2026)
  const contaComanda = useMemo(() => contaDaMesa(selectedMesa), [selectedMesa]);
  // Total dos itens selecionados (divisão por produto — opcional)
  // Produto marcado cobra só o que falta dele — e só as unidades escolhidas
  // quando o operador marcou parte delas (1 de 3 Cocas)
  const selectedItemsTotal = paymentItems.reduce((s, i) => s + valorSelecionado(i), 0);
  const hasProductSelection = showProductSelect && selectedItemsTotal > 0;
  // Se nenhum produto selecionado via toggle, pagar tudo que resta (mesa - já pagos por item - parciais via override)
  const totalJaPagoCalc = paymentItems.reduce((s, i) => s + (i._paid ? valorItem(i) : pagoParcialItem(i)), 0);
  const totalRestante = totalMesa - totalJaPagoCalc - partialPaid;
  // Com a conta dividida, a rodada cobra só a parte da vez
  const parteDivisao = divisao ? parteAtual(divisao) : null;
  // Selecionar produto NUNCA cobra mais do que resta: com R$ 3,00 já pagos numa
  // mesa de R$ 6,00, marcar o produto de 6,00 cobra os 3,00 restantes (v1.5.520).
  const subtotalParaFechar = parteDivisao ? parteDivisao.bruto : hasProductSelection
    ? Math.min(selectedItemsTotal, Math.max(0, totalRestante))
    : Math.max(0, totalRestante);
  // Desconto calculado dinamicamente — espelha lógica do Delivery
  const discountAmount = (() => {
    if (parteDivisao) return parteDivisao.desconto;
    if (discountType === 'none') return 0;
    if (discountType === 'percent') return Math.max(0, subtotalParaFechar * (Math.min(100, Math.max(0, Number(discountValue) || 0)) / 100));
    if (discountType === 'fixed') return Math.max(0, Math.min(subtotalParaFechar, Number(discountValue) || 0));
    if (discountType === 'coupon') return Math.max(0, Math.min(subtotalParaFechar, Number(couponData?.discount || 0)));
    return 0;
  })();
  const totalParaFecharCalculado = parteDivisao ? parteDivisao.valor : Math.max(0, subtotalParaFechar - discountAmount);
  // Cupom no Fechar mesa (achado 412): o servidor faz a conta com os produtos que
  // a rodada está pagando — produto em promoção que não aceita cupom fica fora — e
  // o cupom tem que valer em Mesas. Mudou a rodada, confere de novo.
  const itensDoCupomDaRodada = itensDaRodada(paymentItems, { subtotal: subtotalParaFechar, selecionando: hasProductSelection });
  // Mesa e Mesa QR são módulos separados no cupom (24/10/2026). Mesa que recebeu
  // rodada pelo QR aceita cupom de qualquer um dos dois; só garçom, de Mesas.
  const moduloDoCupomMesa = (selectedMesa?.items || []).some((it) => it && !it.deleted && (it._qid || it._origem === "qr"))
    ? "Mesas,MesaQR" : "Mesas";
  useCupomDaRodada({
    cupom: paymentDialogOpen && discountType === 'coupon' ? couponData : null,
    subtotal: subtotalParaFechar,
    itens: itensDoCupomDaRodada,
    modulo: moduloDoCupomMesa,
    clienteId: selectedMesa?.cliente_id,
    clienteTelefone: selectedMesa?.cliente_telefone,
    pausado: !!divisao,
    aplicar: (cupom) => setCouponData((atual) => ({ ...(atual || {}), ...cupom })),
    remover: (motivo) => {
      setDiscountType('none'); setDiscountValue(0); setCouponData(null);
      toast.error(`Cupom saiu do pagamento: ${motivo}`, { duration: 6000 });
    },
  });
  // Permite override manual digitado pelo lojista no campo Total a pagar.
  // Se o override esta vazio ou invalido, usa o calculado.
  const _totalOverrideNum = totalOverride !== '' ? parseFloat(String(totalOverride).replace(',', '.')) : NaN;
  const totalOverrideAtivo = Number.isFinite(_totalOverrideNum) && _totalOverrideNum >= 0;
  const totalParaFechar = totalOverrideAtivo && !parteDivisao ? _totalOverrideNum : totalParaFecharCalculado;

  // Imprimir fechamento + mover status para "fechamento" (aguardando pagamento)
  const handleFecharMesaDireta = async (mesa) => {
    // Imprimir cupom de fechamento
    try {
      const pedidos = await fetchPedidosMesa(mesa.id, mesa.opened_at);
      await imprimirCupomFechamento(mesa, pedidos, undefined, undefined, undefined, { automatico: true });
    } catch (e) {
      toast.error("Erro ao imprimir cupom: " + (e?.message || ""));
    }
    const updatedMesa = { ...mesa, status: "fechamento" };
    const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
    await saveMesas(updatedMesas);
    toast.success(`Fechamento impresso — Mesa ${mesa.numero} aguardando pagamento.`);
  };

  // Fechar mesa SEM imprimir (v1.5.515): pula direto pro fechamento —
  // move o status e ja' abre o pagamento. O cupom fica pro botao Imprimir.
  const handleFecharMesaSemImprimir = async (mesa) => {
    const updatedMesa = { ...mesa, status: "fechamento" };
    const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
    await saveMesas(updatedMesas);
    handleConfirmarPagamento(updatedMesa);
  };

  // Confirmar pagamento de mesa em fechamento
  const handleConfirmarPagamento = (mesa) => {
    setSelectedMesa(mesa);
    setCart([]);
    setSelectedPayment(null);
    setCashReceived("");
    setShowProductSelect(true);
    // Reset desconto a cada abertura
    setDiscountType('none');
    setDiscountValue(0);
    setCouponData(null);
    setTotalOverride('');
    // Restaurar pagamentos parciais persistidos na mesa (v1.5.520): o que já
    // foi pago fica registrado em mesa.pagamentos e SOBREVIVE a fechar o
    // dialog, reabrir a mesa ou lançar novos produtos. Sem isso o operador
    // perdia o "Pago R$ X" a cada reabertura e podia cobrar de novo.
    let pagSalvo = mesa.pagamentos;
    if (typeof pagSalvo === 'string') { try { pagSalvo = JSON.parse(pagSalvo); } catch { pagSalvo = null; } }
    const logSalvo = Array.isArray(pagSalvo?.log) ? pagSalvo.log : [];
    const pagosSalvos = Array.isArray(pagSalvo?.paid_items) ? pagSalvo.paid_items : [];
    setPartialPaid(Number(pagSalvo?.partial_paid) || 0);
    // Mesa reaberta em Vendas → Pedidos: o desconto do fechamento de antes vem
    // pronto (valor fixo) e os pagamentos voltam na lista com a lixeira
    const descontoReaberto = Number(pagSalvo?.desconto_reaberto?.valor) || 0;
    if (descontoReaberto > 0) {
      setDiscountType('fixed');
      setDiscountValue(descontoReaberto);
    }
    if (pagSalvo?.reaberta) {
      toast.info(`Mesa reaberta por ${pagSalvo.reaberta.por || "—"}: apague na lista o pagamento errado e lance o certo.${descontoReaberto > 0 ? ` O desconto de R$ ${descontoReaberto.toFixed(2).replace(".", ",")} já está aplicado.` : ""}`, { duration: 9000 });
    }
    setPaymentLog(logSalvo);
    setDivisao(pagSalvo?.divisao || null);
    setDescontoAcumulado(pagSalvo?.desconto_acumulado || null);
    // Inicializar lista de produtos para pagamento (re-marcando os já pagos)
    const items = (mesa.items || []).filter(i => !i.deleted).map((item, idx) => {
      const chave = item.cartItemId || `i${idx}`;
      // Texto x número: o PIX do Mesa QR grava a chave como texto
      const jaPago = pagosSalvos.find(pg => String(pg.key) === String(chave));
      return restaurarParcial({
        ...item,
        _idx: idx,
        _paid: !!jaPago,
        _selected: false,
        _paymentInfo: jaPago?.label || null,
      }, pagSalvo?.parciais);
    });
    setPaymentItems(items);
    cobranca.limpar();
    setPaymentDialogOpen(true);
  };

  // Imprimir fechamento + mover status para "fechamento" e volta pra tela principal
  const handleFecharMesa = async () => {
    if (!selectedMesa) return;
    // Se tem itens no carrinho, enviar antes
    if (cart.length > 0) {
      await handleSaveMesaVendas();
    }
    // Pegar mesa atualizada
    const mesaAtual = mesas.find(m => m.id === selectedMesa.id) || selectedMesa;
    // Imprimir cupom de fechamento
    try {
      const pedidos = await fetchPedidosMesa(mesaAtual.id, mesaAtual.opened_at);
      await imprimirCupomFechamento(mesaAtual, pedidos, undefined, undefined, undefined, { automatico: true });
    } catch (e) {
      toast.error("Erro ao imprimir cupom: " + (e?.message || ""));
    }
    const updatedMesa = { ...mesaAtual, status: "fechamento", pessoas: mesaAtual.pessoas || selectedMesa.pessoas || 0 };
    const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
    await saveMesas(updatedMesas);
    toast.success(`Fechamento impresso — Mesa ${selectedMesa.numero} aguardando pagamento.`);
    closeMesaVendas();
  };

  // Um pagamento por vez: o save e a saída do Mesa QR deixam a janela aberta
  // alguns segundos, e um segundo toque no Confirmar cobrava o resto da mesa
  const finalizandoRef = useRef(false);
  // Lixeira em andamento: nada é cobrado até ela terminar (os dois fluxos
  // gravariam um por cima do outro — o pagamento apagado voltaria)
  const apagandoRef = useRef(false);
  const [apagando, setApagando] = useState(false);
  // Edição de parte recusada no blur logo antes do Confirmar: não cobra o
  // valor antigo como se nada tivesse acontecido
  const parteRecusadaRef = useRef(0);
  const finalizeMesaPayment = async (payments, opcoes = {}) => {
    if (finalizandoRef.current || apagandoRef.current) return;
    finalizandoRef.current = true;
    try {
      await finalizeMesaPaymentUmaVez(payments, opcoes);
    } finally {
      finalizandoRef.current = false;
    }
  };

  // Finaliza o pagamento dos itens selecionados (ou todos).
  // `jaRecebido`: o dinheiro já entrou (PIX Online do painel confirmado) —
  // registra sempre, mesmo que a conta tenha mudado pelo celular.
  const finalizeMesaPaymentUmaVez = async (payments, { jaRecebido = false } = {}) => {
    // payments = [{code, label, value}] ou string legacy
    const pms = paymentMethodsFromApi.length > 0 ? paymentMethodsFromApi : fallbackPaymentMethods;
    let paymentsList;
    if (typeof payments === "string") {
      const pm = pms.find((m) => (m.code || m.id || "") === payments);
      paymentsList = [{ code: pm?.code || payments, label: pm?.label || payments, value: totalParaFechar }];
    } else {
      paymentsList = payments;
    }
    // Parte de divisão fica marcada no log (aparece no resumo como "parte 2/3")
    if (divisao) paymentsList = paymentsList.map(p => ({ ...p, parte: `${divisao.pagas + 1}/${divisao.partes}` }));
    const formaLabel = paymentsList.map(p => `${p.label}: R$ ${p.value.toFixed(2).replace(".", ",")}`).join(" + ");
    const formaCode = paymentsList.map(p => p.code).join("+");

    // Mesa QR: produto pago pelo celular com esta janela aberta muda a conta —
    // para aqui (a tela já marca o que foi pago) em vez de cobrar de novo
    const pagosNoCelular = jaRecebido ? [] : await cobranca.pagosAgora(paymentItems);
    if (pagosNoCelular.length) {
      toast.error(`A conta mudou: ${pagosNoCelular.length} produto(s) foram pagos pelo celular ou cancelados. Confira o valor e confirme de novo.`, { duration: 8000 });
      return;
    }

    // Registrar venda no caixa aberto — uma entrada por forma de pagamento
    // (features/mesas-fechamento/lib/vendaNoCaixa.js). Sem caixa aberto, ou
    // com a venda recusada, NADA é gravado na mesa e nenhum pedido conclui
    // (dono, 22/10/2026) — até aqui a tela avisava e seguia, e o dinheiro ficava
    // na mesa e em caixa nenhum (#58524, #43887). O servidor recusa também
    // (api/v1/caixa/trava_mesa.py). "Tudo pago — encerrar a mesa" não recebe
    // nada: não há venda para lançar.
    // Venda de cada forma no caixa ({caixa_id, venda_id}): a lixeira estorna por ela
    let vendasCaixa = [];
    if (paymentsList.some(p => Number(p.value) > 0)) {
      const lancado = await lancarVendasNoCaixa({
        pagamentos: paymentsList,
        pedidoId: selectedMesa.pedido_id || undefined,
        pedidoCodigo: selectedMesa.codigo || String(selectedMesa.mesa_numero || selectedMesa.id),
        token: localStorage.getItem("token"),
      });
      if (!lancado.ok) {
        toast.error(lancado.erro, { duration: 12000 });
        return;
      }
      vendasCaixa = lancado.vendas;
    }

    // Calcula valor pago nesta rodada
    const valorPagoAgora = paymentsList.reduce((s, p) => s + (p.value || 0), 0);
    const tsAgora = new Date().toISOString();
    // Id do pagamento: a lixeira do resumo apaga por ele (e desfaz o `efeito`)
    const pagamentoId = novoIdPagamento();
    const totalMesaBruto = paymentItems.reduce((s, i) => s + valorItem(i), 0);
    const rotuloParte = paymentsList.map(p => `${p.label}`).join(" + ");
    // Divisão: a parte paga se espalha nos produtos dela (o desconto já está
    // dentro das partes). Fora dela, `lancarRodada` (features/mesas-fechamento/
    // lib/rodada.js): por valor, produtos marcados ou um pedaço deles — e o
    // desconto da tela é concedido inteiro AGORA e gravado em
    // `desconto_acumulado`, consumido no que foi pago (achado 503, dono
    // 21/10/2026). Quitar por valor olha o total bruto: o desconto já entrou no
    // `partial_paid` (antes, 10% pagos exatos sobravam "restante" de 2,80).
    const resultadoParte = divisao ? aplicarParte(paymentItems, divisao, rotuloParte, pagamentoId) : null;
    const rodada = divisao ? null : lancarRodada({
      items: paymentItems, partialPaid, descontoAcumulado,
      valorPago: valorPagoAgora, calculado: totalParaFecharCalculado,
      desconto: { valor: discountAmount, tipo: discountType, percentual: discountValue, cupom: couponData?.code || null },
      forma: rotuloParte, formaCompleta: formaLabel, pagamentoId,
    });
    const updatedPaymentItems = resultadoParte ? resultadoParte.items : rodada.items;
    const novaDivisao = resultadoParte ? resultadoParte.divisao : null;
    const novoPartialPaid = resultadoParte ? partialPaid : rodada.partialPaid;
    const novoDescontoAcumulado = resultadoParte
      ? somarDesconto(descontoAcumulado, discountAmount, divisao.desconto)
      : rodada.descontoAcumulado;
    // O que este pagamento fez — é o que a lixeira desfaz. Parte de divisão
    // criada antes desta versão (sem id) não guarda efeito: não se apaga.
    const efeito = divisao && !divisao.id ? null : resultadoParte ? {
      itens: efeitoNosItens(paymentItems, updatedPaymentItems),
      porValor: 0,
      divisaoId: divisao.id,
      concluiu: !novaDivisao,
      ...(!novaDivisao ? { divisaoAntes: divisao } : {}),
      desconto: discountAmount || 0,
    } : rodada.efeito;
    // Desconto concedido agora sai da tela: já está gravado e consumido no que
    // foi pago — aplicar de novo seria em dobro. Se era o da reabertura
    // (Vendas → Pedidos), a mesa deixa de repô-lo ao abrir (`desconto_reaberto: null`).
    const descontoConcedido = !!rodada && rodada.descontoAgora > 0;
    const reabertoConsumido = descontoConcedido && !!lerPagamentos(selectedMesa).desconto_reaberto;
    if (descontoConcedido) {
      setDiscountType('none');
      setDiscountValue(0);
      setCouponData(null);
    }
    // Acumula no log para imprimir no cupom de fechamento
    const novoPaymentLog = [...paymentLog, ...paymentsList.map((p, n) => ({
      ...p, id: pagamentoId, timestamp: tsAgora, ...(n === 0 && efeito ? { efeito } : {}), ...(vendasCaixa[n] ? { caixa: vendasCaixa[n] } : {}),
    }))];
    setPaymentLog(novoPaymentLog);
    setPaymentItems(updatedPaymentItems);
    setDivisao(novaDivisao);
    setDescontoAcumulado(novoDescontoAcumulado);
    setPartialPaid(novoPartialPaid);

    // Checar se TODOS os itens foram pagos
    const allPaid = updatedPaymentItems.every(i => i._paid);

    if (allPaid) {
      // Fechar mesa: atualizar pedidos, imprimir cupom, fechar. O cupom sai
      // DEPOIS de concluir os pedidos: é a conclusão que emite a nota, e com
      // nota saindo o cupom é do servidor — sai só o fiscal (achado 391).
      let cupomDaConta = null;
      try {
        const todosPedidos = await fetchPedidosMesa(selectedMesa.id, selectedMesa.opened_at);
        const pedidosAbertos = todosPedidos.filter(p => !['concluido', 'cancelado'].includes(p.status));
        const totalFinal = updatedPaymentItems.reduce((s, i) => s + (i.final_price ?? i.sale_price ?? 0) * (i.quantity || 1), 0);
        const mesaParaCupom = { ...selectedMesa, total: totalFinal, closed_at: new Date().toISOString() };
        // O cupom sai antes de o desconto chegar aos pedidos (PUT abaixo): leva o
        // acumulado, que já tem o da rodada (`lancarRodada`) e o das partes pagas —
        // o mesmo do PUT (regra do dono 21/10/2026). Rodada zerada = informada: o
        // `desconto_reaberto` não entra de novo (já está no acumulado se foi concedido).
        const descontoDoCupom = {
          descontoRodada: { valor: 0 },
          descontoAcumulado: novoDescontoAcumulado || null,
        };
        cupomDaConta = { mesaParaCupom, pedidosAbertos, descontoDoCupom };
      } catch (e) {
        console.error("Erro ao montar cupom:", e);
      }

      try {
        const todosPedidosMesa = await fetchPedidosMesa(selectedMesa.id, selectedMesa.opened_at);
        const tokUpd = localStorage.getItem("token");
        const headersUpd = tokUpd ? { Authorization: `Bearer ${tokUpd}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
        // Se todos os pagamentos foram feitos com a mesma forma, salvar o code real; caso contrario "multiplo".
        // Olha o LOG COMPLETO (v1.5.520), não só a última rodada: 3,00 no PIX +
        // 3,00 no Dinheiro em rodadas separadas é "multiplo", não "dinheiro".
        const uniqueForms = Array.from(new Set(novoPaymentLog.map(p => p.code)));
        const finalForma = uniqueForms.length === 1 ? uniqueForms[0] : "multiplo";

        // Desconto aplicado no fechamento da mesa: vai para o MAIOR pedido aberto
        // (um alerta de desconto no WhatsApp e a origem registrada); só o que não
        // couber nele passa para o seguinte — `descontoPorPedido` (achado 429).
        const pedidosAbertos = todosPedidosMesa.filter(p => !['concluido', 'cancelado'].includes(p.status));
        // Todo o desconto da sessão está no acumulado: o da rodada entrou nele em
        // `lancarRodada`, o da divisão parte a parte — nada é somado duas vezes
        const descontoFinal = descontoDoFechamento(null, novoDescontoAcumulado);
        const aplicouDesconto = descontoFinal.total > 0;
        // Reconciliação (v1.5.370): o que o cliente PAGOU (soma dos pagamentos
        // registrados) é a verdade. Se a mesa tem UM pedido e o total dele
        // diverge (ex.: #93099 inflado por append duplicado ficou R$ 583,80
        // com consumo real de R$ 194,60), o total do pedido assume o pago —
        // senão o faturamento reporta receita fantasma.
        const totalCobradoMesa = novoPaymentLog.reduce((s, p) => s + (Number(p.value) || 0), 0);

        const descontoDeCada = aplicouDesconto
          ? descontoPorPedido(descontoFinal.total, pedidosAbertos.map(p => Number(p.total || 0)))
          : [];
        const payloads = pedidosAbertos.map((ped, i) => {
          const payload = { status: "concluido", forma_pagamento: finalForma };
          const descontoDeste = descontoDeCada[i] || 0;
          if (descontoDeste > 0) {
            // Reduz o total deste pedido pela parte dele no desconto e anexa metadados
            payload.total = Math.max(0, Math.round((Number(ped.total || 0) - descontoDeste) * 100) / 100);
            payload.desconto_tipo = descontoFinal.tipo;
            if (descontoFinal.tipo === 'percent' || descontoFinal.tipo === 'fixed') {
              payload.desconto_manual = descontoDeste;
              payload.desconto_percentual = descontoFinal.tipo === 'percent' ? descontoFinal.percentual : null;
            } else if (descontoFinal.tipo === 'coupon') {
              payload.cupom_codigo = (descontoFinal.cupom || '').toUpperCase();
              payload.cupom_desconto = descontoDeste;
            }
          }
          // Mesa com UM pedido: total do pedido = total efetivamente pago
          // (já líquido de desconto — os pagamentos são pós-desconto).
          // Mesa com 2+ pedidos: sem base pra ratear, mantém totais originais.
          if (pedidosAbertos.length === 1 && totalCobradoMesa > 0 &&
              Math.abs(Number(ped.total || 0) - totalCobradoMesa) > 0.01) {
            payload.total = totalCobradoMesa;
          }
          return payload;
        });
        // Dividido: cada pedido grava quanto entrou em cada forma (o log da mesa
        // é apagado ao fechar; sem isto a tela de Pedidos só dizia "Múltiplo")
        if (finalForma === "multiplo") {
          const totais = payloads.map((pl, i) => pl.total ?? Number(pedidosAbertos[i].total || 0));
          partesPorPedido(novoPaymentLog, totais).forEach((partes, i) => {
            if (partes.length) payloads[i].pagamentos = partes;
          });
        }
        for (let i = 0; i < pedidosAbertos.length; i++) {
          await axios.put(`${API}/pedidos/${pedidosAbertos[i].id}`, payloads[i], { headers: headersUpd });
        }
      } catch (e) {
        console.error("Erro ao atualizar pedidos da mesa:", e);
        toast.error(e?.response?.data?.detail || "Erro ao finalizar pedidos. Tente fechar a mesa novamente.");
        return;
      }

      if (cupomDaConta) {
        try {
          const { mesaParaCupom, pedidosAbertos, descontoDoCupom } = cupomDaConta;
          if (!(await servidorImprimeCaixa(pedidosAbertos[0]?.id))) {
            await imprimirCupomFechamento(mesaParaCupom, pedidosAbertos, undefined, novoPaymentLog, undefined, { ...descontoDoCupom, automatico: true });
          }
        } catch (e) {
          console.error("Erro ao imprimir cupom:", e);
        }
      }

      const totalFinal = updatedPaymentItems.reduce((s, i) => s + (i.final_price ?? i.sale_price ?? 0) * (i.quantity || 1), 0);
      toast.success(`Mesa fechada! R$ ${totalFinal.toFixed(2).replace(".", ",")} — todos os itens pagos.`);

      const uniqueFormsCheck = Array.from(new Set(novoPaymentLog.map(p => p.code)));
      const updatedMesa = {
        ...selectedMesa,
        status: "fechada",
        items: [],
        total: 0,
        cliente: "",
        cliente_id: null,
        comanda: null,
        codigo: null,
        pedido_id: null,
        pessoas: 0,
        opened_at: null,
        closed_at: new Date().toISOString(),
        forma_pagamento: uniqueFormsCheck.length === 1 ? uniqueFormsCheck[0] : "multiplo",
        pagamentos: null,
        // Mesa QR: só o fechamento PAGO credita o cashback do que faltava
        // (cancelar e transferir também gravam a mesa como "fechada")
        fechamento_pago: true,
        // Qual sessão este fechamento encerra: o servidor não deixa uma tela
        // velha fechar a sessão que um cliente do QR abriu depois
        sessao_fechada: selectedMesa.opened_at || null,
      };

      const updatedMesas = mesas.map((m) => (m.id === updatedMesa.id ? updatedMesa : m));
      saveMesas(updatedMesas);
      setPaymentDialogOpen(false);
      setSelectedPayment(null);
      setCashReceived("");
      setShowPixModal(false);
      setPixOrderId(null);
      closeMesaVendas();
    } else {
      // Pagamento parcial — resetar pagamento, manter dialog aberto. O restante
      // é bruto − quitado: o desconto concedido já está dentro do quitado.
      const totalRestanteAposPag = Math.max(0, totalMesaBruto - quitadoDaMesa(updatedPaymentItems, novoPartialPaid));
      if (novaDivisao) {
        const faltam = novaDivisao.partes - novaDivisao.pagas;
        toast.success(`Parte ${resultadoParte.parte} paga (${formaLabel}) · ${faltam === 1 ? 'falta 1 parte' : `faltam ${faltam} partes`}`);
      } else {
        const brlTxt = (v) => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;
        const avisoDesconto = descontoConcedido ? ` · desconto de ${brlTxt(rodada.descontoAgora)} gravado na mesa` : '';
        toast.success(`Pago ${brlTxt(valorPagoAgora)} (${formaLabel}) · Restante ${brlTxt(totalRestanteAposPag)}${avisoDesconto}`, descontoConcedido ? { duration: 7000 } : undefined);
      }
      // Persistir o parcial na mesa (v1.5.520): sobrevive a fechar o dialog,
      // reabrir a mesa e lançar novos produtos. O registro no caixa já foi
      // feito acima — o que salvamos aqui é só o estado de cobrança (com o
      // desconto concedido, no mesmo PUT que grava o pagamento).
      await salvarPagamentosMesa(montarPagamentos({
        log: novoPaymentLog,
        partialPaid: novoPartialPaid,
        items: updatedPaymentItems,
        divisao: novaDivisao,
        descontoAcumulado: novoDescontoAcumulado,
        ...(reabertoConsumido ? { descontoReaberto: null } : {}),
      }));
      // Mesa QR: cobrou tudo de uma pessoa no caixa → a saída dela sai aqui
      await cobranca.aposPagamento(updatedPaymentItems);
      setSelectedPayment(null);
      setCashReceived("");
      setShowProductSelect(true); // mostrar produtos para próxima seleção
      setTotalOverride(''); // reset override do total
    }
  };

  // Com produtos marcados, o valor digitado paga só um pedaço deles — mais do
  // que eles valem não tem para onde ir (o resto da mesa não foi marcado)
  const valorPassaDosMarcados = () => {
    if (divisao || selectedCount === 0 || !totalOverrideAtivo || !(_totalOverrideNum > totalParaFecharCalculado + 0.005)) return false;
    toast.error(`O valor passa do total dos produtos marcados (R$ ${totalParaFecharCalculado.toFixed(2).replace('.', ',')}). Para receber mais, desmarque os produtos.`);
    return true;
  };

  const confirmPayment = async () => {
    // Divisão de pagamento (v1.5.520): sem modal — o operador edita o "Total a
    // pagar" e confirma forma a forma; cada parcial fica no resumo até quitar.
    if (!selectedPayment) {
      toast.error("Selecione uma forma de pagamento!");
      return;
    }
    if (apagandoRef.current || Date.now() - parteRecusadaRef.current < 1500) return;
    if (valorPassaDosMarcados()) return;
    const paymentCode = (selectedPayment || "").toLowerCase();
    // Por substring (v1.5.521): o code real é "dinheiro_<uuid do tenant>" — o
    // exact-match nunca casava, então troco/valor recebido saíam ZERADOS do
    // pagamento e o Confirmar não validava o recebido. Família do bug v1.5.517.
    const isDinheiro = paymentCode.includes("dinheiro");
    if (isDinheiro && (!cashReceived || parseFloat(cashReceived) < totalParaFechar)) {
      toast.error("Informe o valor recebido.");
      return;
    }

    // PIX Online — mesmo caminho do popup de confirmacao
    if (paymentCode.includes("pix_online")) {
      await prepararPixOnline();
      return;
    }

    const pms = paymentMethodsFromApi.length > 0 ? paymentMethodsFromApi : fallbackPaymentMethods;
    const pm = pms.find((m) => (m.code || m.id || "") === selectedPayment);
    const trocoValor = isDinheiro && cashReceived && parseFloat(cashReceived) > totalParaFechar
      ? +(parseFloat(cashReceived) - totalParaFechar).toFixed(2)
      : null;
    await finalizeMesaPayment([{
      code: pm?.code || selectedPayment,
      label: pm?.label || selectedPayment,
      value: totalParaFechar,
      troco_precisa: !!trocoValor,
      troco_valor: trocoValor,
      cash_received: isDinheiro && cashReceived ? parseFloat(cashReceived) : null,
    }]);
  };

  // PIX Online (09/09/2026): cobrança AVULSA do valor que falta pagar. O
  // pedido da mesa fica intocado — antes a tela marcava o pedido antigo como
  // pix_online e o monitor de expiração cancelava a mesa em 30 s, além de
  // cobrar o total do pedido em vez do saldo. Chamada pelo popup "Gerar QR
  // Code" (clique na forma) e pelo Confirmar.
  const prepararPixOnline = async () => {
    try {
      if (!(totalParaFechar > 0)) {
        toast.error("Não há valor a cobrar.");
        return;
      }
      if (apagandoRef.current || Date.now() - parteRecusadaRef.current < 1500) return;
      if (valorPassaDosMarcados()) return;
      // Sem caixa aberto o QR nem é gerado (22/10/2026): depois que o cliente
      // paga, o dinheiro entra de qualquer jeito e a mesa não poderia receber
      if (!(await caixaAbertoAgora({ token: localStorage.getItem("token") }))) {
        toast.error(SEM_CAIXA, { duration: 10000 });
        return;
      }
      // Mesa QR: confere antes de gerar o QR — depois que o cliente paga, o
      // dinheiro entra de qualquer jeito
      const mudou = await cobranca.pagosAgora(paymentItems);
      if (mudou.length) {
        toast.error(`A conta mudou: ${mudou.length} produto(s) foram pagos pelo celular ou cancelados. Confira o valor antes de gerar o PIX.`, { duration: 8000 });
        return;
      }
      const pedidos = await fetchPedidosMesa(selectedMesa.id, selectedMesa.opened_at);
      const abertos = pedidos.filter(p => !['concluido', 'cancelado'].includes(p.status));
      if (abertos.length === 0) {
        toast.error("Nenhum pedido em aberto nesta mesa");
        return;
      }
      setPixOrderId(abertos[0].id);
      setPixValor(totalParaFechar);
      setPaymentDialogOpen(false);
      setShowPixModal(true);
    } catch (e) {
      toast.error("Erro ao preparar PIX: " + (e?.message || "tente novamente."));
    }
  };

  // PIX Online pago: registra como qualquer outra forma. Se o valor cobre o
  // que faltava, finalizeMesaPayment fecha a mesa; se foi parcial, fica no
  // resumo como pagamento parcial. O code é o da forma cadastrada na loja
  // (contém "pix_online"; o exato tem sufixo do tenant — v1.5.517).
  const handlePixConfirmed = async () => {
    setShowPixModal(false);
    const pms = paymentMethodsFromApi.length > 0 ? paymentMethodsFromApi : fallbackPaymentMethods;
    const pm = pms.find((m) => /pix_online/i.test(`${m.code || m.id || ""}`));
    await finalizeMesaPayment([{
      code: pm?.code || pm?.id || "pix_online",
      label: pm?.label || "PIX Online",
      value: pixValor,
      troco_precisa: false,
      troco_valor: null,
      cash_received: null,
    }], { jaRecebido: true });
    setPixOrderId(null);
  };

  // Grava o estado de cobrança na mesa (o caixa já foi lançado à parte)
  const salvarPagamentosMesa = (pag) => {
    const mesaComPag = { ...selectedMesa, pagamentos: pag };
    setSelectedMesa(mesaComPag);
    return saveMesas(mesas.map(m => (m.id === mesaComPag.id ? mesaComPag : m)));
  };

  // DIVIDIR: produtos marcados, ou o que falta da mesa inteira quando nada
  // está marcado. O desconto já aplicado entra no valor das partes.
  const iniciarDivisao = (partes) => {
    const selecionados = paymentItems.filter(i => i._selected && !i._paid);
    const mesaInteira = selecionados.length === 0;
    const liquido = totalParaFecharCalculado;
    if (!(liquido > 0)) { toast.error("Não há valor para dividir."); return; }
    const nova = criarDivisao({
      itens: mesaInteira ? paymentItems.filter(i => !i._paid) : selecionados,
      bruto: subtotalParaFechar,
      liquido,
      partes,
      mesaInteira,
      desconto: discountAmount > 0 ? {
        tipo: discountType,
        percentual: discountType === 'percent' ? Number(discountValue || 0) : null,
        cupom: couponData?.code || null,
      } : null,
    });
    const itensSemSelecao = paymentItems.map(i => ({ ...i, _selected: false }));
    setPaymentItems(itensSemSelecao);
    setDivisao(nova);
    setDiscountType('none');
    setDiscountValue(0);
    setCouponData(null);
    setTotalOverride('');
    // O desconto da reabertura que entrou nas partes não volta ao abrir a
    // janela de novo (senão, cancelada a divisão, ele seria aplicado em dobro)
    const reabertoNaDivisao = !!nova.desconto && !!lerPagamentos(selectedMesa).desconto_reaberto;
    salvarPagamentosMesa(montarPagamentos({ log: paymentLog, partialPaid, items: itensSemSelecao, divisao: nova, descontoAcumulado, ...(reabertoNaDivisao ? { descontoReaberto: null } : {}) }));
  };

  // "O segundo quer pagar R$ 10": muda a parte e as que faltam dividem o resto
  const editarParteDivisao = (indice, valor) => {
    if (!divisao) return false;
    const r = editarParte(divisao, indice, valor);
    if (r.erro) { parteRecusadaRef.current = Date.now(); toast.error(r.erro); return false; }
    setDivisao(r.divisao);
    salvarPagamentosMesa(montarPagamentos({ log: paymentLog, partialPaid, items: paymentItems, divisao: r.divisao, descontoAcumulado }));
    return true;
  };

  // Lixeira do resumo (dono, 24/09/2026): estorna a venda no caixa e desfaz o
  // que o pagamento fez na mesa (produtos, parte da divisão, "por valor").
  // O caixa vem primeiro: estorno recusado (caixa fechado, sem permissão)
  // deixa a mesa como está.
  const apagarPagamento = async (id) => {
    if (apagandoRef.current || finalizandoRef.current) { toast.info("Aguarde o pagamento em andamento terminar."); return; }
    const r = desfazerPagamento({ log: paymentLog, items: paymentItems, divisao }, id);
    if (r.erro) { toast.error(r.erro, { duration: 8000 }); return; }
    apagandoRef.current = true;
    setApagando(true);
    // O polling do Mesa QR fica parado até a mesa gravar: uma consulta no meio
    // traria de volta, como pago, o produto que acabou de sair
    cobranca.pausar(true);
    try {
      const vendas = r.entradas.filter((e) => e.caixa?.venda_id);
      let estornadas = 0;
      try {
        for (const e of vendas) {
          await axios.post(`${API}/caixa/${e.caixa.caixa_id}/movimentos/${e.caixa.venda_id}/estornar`, {}, getAuthHeader());
          estornadas += 1;
        }
      } catch (err) {
        const motivo = err?.response?.data?.detail || "Não consegui estornar a venda no caixa.";
        toast.error(estornadas ? `${motivo} Parte já foi estornada: toque na lixeira de novo.` : `${motivo} Nada foi apagado.`, { duration: 9000 });
        return;
      }
      // O "por valor" e o desconto que este pagamento concedeu saem do registro
      // (lib/rodada.js); o que ele fez nos produtos já voltou em `r.items`
      const { partialPaid: novoPartial, descontoAcumulado: novoDesconto } = desfazerNaRodada({ partialPaid, descontoAcumulado }, r.efeito);
      // Grava a mesa ANTES de mudar a tela: se falhar, tudo fica como estava e
      // a lixeira pode ser usada de novo (estornar de novo não devolve duas vezes)
      const mesaComPag = { ...selectedMesa, pagamentos: montarPagamentos({ log: r.log, partialPaid: novoPartial, items: r.items, divisao: r.divisao, descontoAcumulado: novoDesconto }) };
      const gravou = await saveMesas(mesas.map((m) => (m.id === mesaComPag.id ? mesaComPag : m)));
      if (!gravou) {
        toast.error(`${vendas.length ? "A venda foi estornada no caixa, mas não" : "Não"} consegui salvar a mesa. Toque na lixeira de novo.`, { duration: 9000 });
        return;
      }
      setSelectedMesa(mesaComPag);
      setPaymentLog(r.log);
      setPaymentItems(r.items);
      setDivisao(r.divisao);
      setPartialPaid(novoPartial);
      setDescontoAcumulado(novoDesconto);
      setSelectedPayment(null);
      setCashReceived("");
      setTotalOverride('');
      const valor = r.entradas.reduce((s, e) => s + (Number(e.value) || 0), 0);
      const descontoDesfeito = Number(r.efeito.desconto) > 0 ? ` O desconto de R$ ${Number(r.efeito.desconto).toFixed(2).replace('.', ',')} concedido com ele saiu junto: aplique de novo se precisar.` : '';
      toast.success(`Pagamento de R$ ${valor.toFixed(2).replace('.', ',')} (${r.entradas[0]?.label || 'forma'}) apagado${vendas.length ? ' e estornado no caixa' : ''}.${descontoDesfeito}`, descontoDesfeito ? { duration: 9000 } : undefined);
    } finally {
      apagandoRef.current = false;
      setApagando(false);
      cobranca.invalidar();
      cobranca.pausar(false);
      cobranca.recarregar();
    }
  };

  // O que já foi pago continua nos produtos; o resto volta a ser cobrado normal
  const cancelarDivisao = () => {
    if (divisao?.desconto && divisao.pagas < divisao.partes) {
      toast.info("O desconto da divisão valeu só para as partes já pagas. Aplique de novo se precisar.");
    }
    setDivisao(null);
    salvarPagamentosMesa(montarPagamentos({ log: paymentLog, partialPaid, items: paymentItems, divisao: null, descontoAcumulado }));
  };

  // Mesa reaberta (Vendas → Pedidos) já quitada pelos pagamentos da lista
  const reabertaQuitada = !!selectedMesa?.pagamentos?.reaberta && !divisao && paymentLog.length > 0
    && !hasProductSelection && totalParaFechar <= 0.009;
  const canSubmitNormal = selectedPayment && !(/dinheiro/i.test(selectedPayment || "") && (!cashReceived || parseFloat(cashReceived) < totalParaFechar));

  // Helpers para seleção de produtos no pagamento
  const togglePaymentItem = (idx) => {
    if (divisao) return;
    setPaymentItems(prev => prev.map((item, i) => i === idx ? { ...item, _selected: !item._selected, _selQtd: null } : item));
  };
  // Quantas unidades de um produto repetido entram na seleção (null = todas)
  const setQtdSelecionada = (idx, qtd) => {
    setPaymentItems(prev => prev.map((item, i) => i === idx ? { ...item, _selected: true, _selQtd: qtd } : item));
  };
  const selectAllUnpaidItems = () => {
    const allUnpaidSelected = paymentItems.filter(i => !i._paid).every(i => i._selected);
    setPaymentItems(prev => prev.map(item => item._paid ? item : { ...item, _selected: !allUnpaidSelected, _selQtd: null }));
  };
  const selectedCount = paymentItems.filter(i => i._selected && !i._paid).length;
  const totalGeralMesa = paymentItems.reduce((s, i) => s + valorItem(i), 0);
  // Desconto concedido em pagamentos anteriores desta sessão (achado 503): está
  // dentro do quitado (produtos e por valor); o "Pago" mostrado é só o dinheiro
  const descontoGravado = descontoJaConcedido(descontoAcumulado);
  const pagoEmDinheiro = Math.max(0, totalJaPagoCalc + partialPaid - descontoGravado);

  const paymentMethods = paymentMethodsFromApi.length > 0 ? paymentMethodsFromApi : fallbackPaymentMethods;

  // "Imprimir fechamento" de dentro da tela de pagamento: o desconto da tela só
  // é gravado ao concluir — sem passar aqui, o cupom saía sem ele (Mesa 99, 21/10/2026)
  const imprimirFechamentoDaTela = async () => {
    if (!selectedMesa) return;
    try {
      const pedidos = await fetchPedidosMesa(selectedMesa.id, selectedMesa.opened_at);
      await imprimirCupomFechamento(selectedMesa, pedidos, undefined, paymentLog, undefined, {
        descontoRodada: descontoEmAndamento({ tipo: discountType, valor: discountValue, cupom: couponData, base: totalRestante, divisao, log: paymentLog }),
        descontoAcumulado: descontoAcumulado || null,
      });
      toast.success("Fechamento enviado para a impressora.");
    } catch (e) {
      toast.error("Erro ao imprimir: " + (e?.message || ""));
    }
  };

  // Tudo que o popup de pagamento precisa — ele só apresenta; as ações ficam aqui
  const fechamentoProps = {
    open: paymentDialogOpen,
    onOpenChange: setPaymentDialogOpen,
    onImprimirFechamento: imprimirFechamentoDaTela,
    mesa: selectedMesa,
    mesaRotulo: selectedMesa ? mesaLabelShort(selectedMesa) : "",
    getImageUrl,
    pmVisual,
    produtos: {
      itens: paymentItems,
      onToggle: togglePaymentItem,
      onQtd: setQtdSelecionada,
      onToggleTodos: selectAllUnpaidItems,
      totalPago: pagoEmDinheiro,
      descontoGravado,
      totalMesa: totalGeralMesa,
      selecionadosQtd: selectedCount,
      selecionadosTotal: selectedItemsTotal,
      // Mesa QR: botões "cobrar por pessoa" em cima da lista
      extraTopo: (
        <>
          {reabertaQuitada && <FecharReaberta desconto={discountAmount} ocupado={apagando} onFechar={() => finalizeMesaPayment([])} />}
          <PagarPorPessoa cobranca={cobranca} itens={paymentItems} divisaoAtiva={!!divisao}
            onEncerrar={() => finalizeMesaPayment([])} />
        </>
      ),
      // De quem é cada produto e quem lançou (dono, 24/09/2026)
      rodapeItem: (item) => <QuemPediu className="mt-1.5" fotoDe={getImageUrl} info={quemPediu(item, cobranca)} />,
    },
    divisao: {
      atual: divisao,
      parte: parteDivisao,
      valorBase: parteDivisao ? 0 : totalParaFecharCalculado,
      temDesconto: discountAmount > 0,
      onDividir: iniciarDivisao,
      onCancelar: cancelarDivisao,
      onEditarParte: editarParteDivisao,
    },
    total: {
      subtotal: subtotalParaFechar,
      desconto: discountAmount,
      calculado: totalParaFecharCalculado,
      override: totalOverride,
      setOverride: setTotalOverride,
      overrideAtivo: totalOverrideAtivo,
      overrideNum: _totalOverrideNum,
      pago: pagoEmDinheiro,
      descontoGravado,
      restante: Math.max(0, totalGeralMesa - totalJaPagoCalc - partialPaid),
      log: paymentLog,
      onApagar: apagarPagamento,
    },
    desconto: {
      tipo: discountType,
      valor: discountValue,
      cupom: couponData,
      montante: discountAmount,
      clienteId: selectedMesa?.cliente_id,
      clienteTelefone: selectedMesa?.cliente_telefone,
      itens: itensDoCupomDaRodada,
      modulo: moduloDoCupomMesa,
      onAplicar: ({ tipo, valor, cupom }) => {
        setDiscountType(tipo);
        setDiscountValue(valor || 0);
        setCouponData(cupom || null);
      },
      onRemover: () => {
        setDiscountType('none');
        setDiscountValue(0);
        setCouponData(null);
      },
    },
    pagamento: {
      formas: paymentMethods,
      selecionada: selectedPayment,
      setSelecionada: setSelectedPayment,
      recebido: cashReceived,
      setRecebido: setCashReceived,
      abrirPix: () => setShowPixConfirm(true),
      abrirTroco: () => setShowTrocoModal(true),
      total: totalParaFechar,
      confirmar: confirmPayment,
      podeConfirmar: !!canSubmitNormal && !apagando,
    },
  };

  // Clientes filtrados para autocomplete (busca na aba CLIENTES)
  // Busca no servidor enquanto digita (nome, telefone, CPF ou e-mail), com
  // uma pausa de 250 ms entre teclas
  const termoCliente = (clienteNome || "").trim();
  useEffect(() => {
    if (!mesaVendasOpen || selectedClienteId || termoCliente.length < 2) return undefined;
    let vivo = true;
    const t = setTimeout(() => {
      axios.get(`${API}/clientes/search/${encodeURIComponent(termoCliente)}?leve=1&limite=8`, getAuthHeader())
        .then(({ data }) => { if (vivo) setBuscaCliente({ termo: termoCliente, lista: Array.isArray(data) ? data : [] }); })
        .catch(() => { if (vivo) setBuscaCliente({ termo: termoCliente, lista: [] }); });
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [termoCliente, selectedClienteId, mesaVendasOpen]);
  const buscandoCliente = !selectedClienteId && termoCliente.length >= 2 && buscaCliente.termo !== termoCliente;
  const filteredClientes = useMemo(() => {
    if (termoCliente.length < 2 || buscaCliente.termo !== termoCliente) return [];
    return buscaCliente.lista;
  }, [buscaCliente, termoCliente]);

  const formatPhone = (raw) => {
    const n = raw.replace(/\D/g, "");
    if (n.length <= 2) return `(${n}`;
    if (n.length <= 3) return `(${n.slice(0,2)}) ${n.slice(2)}`;
    if (n.length <= 7) return `(${n.slice(0,2)}) ${n.slice(2,3)}.${n.slice(3)}`;
    return `(${n.slice(0,2)}) ${n.slice(2,3)}.${n.slice(3,7)}-${n.slice(7,11)}`;
  };

  const openNovoClienteModal = () => {
    const text = (clienteNome || "").trim();
    const isPhone = /^\d{7,}$/.test(text.replace(/\D/g, ""));
    setNovoClienteTelefone(isPhone ? formatPhone(text) : "");
    setNovoClienteNome(isPhone ? "" : text);
    setNovoClienteCpf("");
    setNovoClienteNascimento("");
    setShowCadastrarCliente(true);
    setShowCadastrarForm(true);
  };

  const closeNovoClienteModal = () => {
    setShowCadastrarCliente(false);
    setShowCadastrarForm(false);
    setNovoClienteNome("");
    setNovoClienteTelefone("");
    setNovoClienteCpf("");
    setNovoClienteNascimento("");
  };

  const handleCadastrarNovoCliente = async () => {
    const nome = novoClienteNome?.trim();
    if (!nome) { toast.error("Informe o nome do cliente."); return; }
    setCadastrandoCliente(true);
    try {
      const body = {
        nome,
        telefone: (novoClienteTelefone || "").trim() || null,
        cpf: (novoClienteCpf || "").trim() || null,
        data_nascimento: (novoClienteNascimento || "").trim() || null,
      };
      const { data: created } = await axios.post(`${API}/clientes`, body, getAuthHeader());
      setClientes((prev) => [...(prev || []), created]);
      setClienteNome(created.nome || "");
      setSelectedClienteId(created.id);
      closeNovoClienteModal();
      toast.success("Cliente cadastrado!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cadastrar cliente.");
    } finally {
      setCadastrandoCliente(false);
    }
  };

  // Se tela de vendas está aberta, mostrar ela
  // Espaço numa mesa: abre o pagamento. Em fechamento, direto; aberta com
  // produto, passa para fechamento (sem imprimir) e abre — o Fechar mesa do cartão.
  const pagarMesa = (mesa) => {
    if (!mesa) return;
    if (mesa.status === "fechamento") { handleConfirmarPagamento(mesa); return; }
    if (mesa.status === "aberta" && (mesa.items || []).some((i) => !i.deleted)) { handleFecharMesaSemImprimir(mesa); return; }
    toast.warning(`Mesa ${mesa.numero} sem produto para fechar`);
  };
  // Espaço dentro da mesa: guarda o que foi digitado, fecha a tela e abre o pagamento
  const pagarDentro = async () => {
    if (!selectedMesa) return;
    if (cart.length) { toast.warning("Envie (Enter) ou tire os itens do carrinho antes de fechar a mesa"); return; }
    if (!((selectedMesa.items || []).some((i) => !i.deleted))) { toast.warning("A mesa não tem produto para fechar"); return; }
    setPagarAoSair(selectedMesa.id);
    if (await salvarInfoAoVoltar()) closeMesaVendas();
    else setPagarAoSair(null);
  };
  const tecladoMesas = useRef(null);
  tecladoMesas.current = {
    aberta: mesaVendasOpen && !!selectedMesa, cart, mesas, foco: mesaFoco, setFoco: setMesaFoco,
    abrir: handleMesaClick, voltar: voltarDaMesa, enviar: requestEnvio, pagarDentro, pagarMesa,
  };
  useTecladoMesas(tecladoMesas);
  useEffect(() => {
    if (!pagarAoSair || mesaVendasOpen) return;
    const mesa = mesas.find((m) => m.id === pagarAoSair);
    setPagarAoSair(null);
    if (mesa) pagarMesa(mesa);
  }, [pagarAoSair, mesaVendasOpen, mesas]); // eslint-disable-line react-hooks/exhaustive-deps

  if (mesaVendasOpen && selectedMesa) {
    return (
      <div className="h-[100dvh] min-h-[100dvh] flex flex-col bg-background overflow-hidden">
        <NovaPessoaDialog aberto={donos.novaPara != null} onFechar={donos.fecharNova} onCriar={donos.criarPessoa} />
        <ConfirmarCancelarItem
          item={itemParaCancelar?.item}
          produtos={allProducts}
          // Produto já entregue: a janela avisa e oferece o igual que ainda não
          // chegou na mesa (dono, 03/10/2026)
          contexto={itemParaCancelar ? contextoDoCancelamento(selectedMesa?.items, itemParaCancelar.idx, situacaoDe) : null}
          trocado={!!itemParaCancelar?.trocado}
          onTrocar={(linha) => setItemParaCancelar({ idx: linha.idx, item: linha.item, trocado: true })}
          onFechar={() => setItemParaCancelar(null)}
          onConfirmar={(qtd) => { const alvo = itemParaCancelar; setItemParaCancelar(null); if (alvo) toggleItemDeleted(alvo.idx, qtd); }}
        />
        <SairSemEnviarDialog
          aberto={sairSemEnviarOpen}
          quantidade={cart.length}
          onContinuar={() => setSairSemEnviarOpen(false)}
          onEnviar={() => { setSairSemEnviarOpen(false); requestEnvio(); }}
          onSair={async () => {
            setSairSemEnviarOpen(false);
            if (await salvarInfoAoVoltar()) closeMesaVendas();
          }}
        />
        {/* Header — mobile: back+título; Comanda+Cliente em coluna; safe-area no topo para notch; min-[360px] para telas 320–360px */}
        <div className="px-2 min-[360px]:px-3 sm:px-4 py-3 sm:py-4 pt-[max(0.75rem,env(safe-area-inset-top,0px))] border-b bg-card flex flex-col gap-2 min-[360px]:gap-3 shrink-0">
          <div className="flex items-center gap-2 min-[360px]:gap-3 shrink-0">
            <Button variant="ghost" size="icon" className="h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 touch-manipulation" onClick={voltarDaMesa} data-e2e="mesa-voltar">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0 shrink-0 sm:flex-1 max-w-[40%] sm:max-w-none">
              <h2 className="text-base min-[360px]:text-lg sm:text-xl font-bold text-foreground truncate">Mesa {mesaLabelShort(selectedMesa)}</h2>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{selectedMesa.nome && /^Mesa\s+\S/i.test(selectedMesa.nome) ? '' : selectedMesa.nome}</p>
            </div>
            {/* Celular: informações da mesa na linha do título, abre/fecha o formulário
                (dono, 25/09/2026 — a barra em linha própria roubava espaço do cardápio) */}
            <ResumoInfoMesa compacto className="flex-1 sm:hidden" comanda={comandaVal} pessoas={pessoasVal} cliente={clienteNome}
              observacao={mesaObservacao} aberto={mesaInfoExpanded} onAlternar={() => setMesaInfoExpanded((v) => !v)} />
          </div>
          {/* Barra larga do computador (no celular o resumo fica na linha do título) */}
          {!mesaInfoExpanded && (
            <ResumoInfoMesa className="hidden sm:flex" comanda={comandaVal} pessoas={pessoasVal} cliente={clienteNome}
              observacao={mesaObservacao} aberto={false} onAlternar={() => setMesaInfoExpanded(true)} />
          )}

          {/* Form expandido — condicional em todos os breakpoints */}
          <div className={`${mesaInfoExpanded ? 'flex' : 'hidden'} flex-row flex-wrap sm:items-end gap-2 min-[360px]:gap-3 w-full`}>
            {/* Só no computador: no celular o resumo do cabeçalho já abre e fecha */}
            <div className="hidden sm:flex items-center justify-between mb-1 sm:w-auto sm:order-last sm:ml-auto">
              <button
                type="button"
                onClick={() => setMesaInfoExpanded(false)}
                className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-semibold"
              >
                Recolher <ChevronDown className="w-3.5 h-3.5 rotate-180" />
              </button>
            </div>
            <div className="w-[calc(50%-0.375rem)] sm:w-28 flex-shrink-0">
              <label className="text-xs font-semibold text-orange-600 dark:text-orange-400 block mb-0.5 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                Comanda
              </label>
              <Input
                placeholder="Nº"
                value={comandaVal}
                onChange={(e) => setComandaVal(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                className="h-11 min-h-[44px] sm:h-9 sm:min-h-0 text-sm border-orange-400/50 dark:border-orange-500/40 focus:ring-orange-500 focus:border-orange-500 bg-orange-50/50 dark:bg-orange-500/5"
              />
            </div>
            <div className="w-[calc(50%-0.375rem)] sm:w-20 flex-shrink-0">
              <label className="text-xs font-semibold text-orange-600 dark:text-orange-400 block mb-0.5 flex items-center gap-1">
                <Users className="w-3 h-3" />
                Pessoas
              </label>
              <Input
                placeholder="0"
                value={pessoasVal}
                onChange={(e) => setPessoasVal(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                className="h-11 min-h-[44px] sm:h-9 sm:min-h-0 text-sm border-orange-400/50 dark:border-orange-500/40 focus:ring-orange-500 focus:border-orange-500 bg-orange-50/50 dark:bg-orange-500/5"
              />
            </div>
            <div className="relative flex-1 min-w-0 w-full sm:max-w-[208px]">
              <label className="text-xs font-semibold text-orange-600 dark:text-orange-400 block mb-0.5 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                Cliente
              </label>
              <Input
                placeholder="Cliente (buscar)"
                value={clienteNome}
                onChange={(e) => { const v = e.target.value; const digits = v.replace(/\D/g, ""); const isPhone = digits.length > 0 && digits.length === v.replace(/[\s().\-]/g, "").length; setClienteNome(isPhone ? formatPhone(v) : v); setSelectedClienteId(null); setShowCadastrarCliente(false); setShowCadastrarForm(false); setNovoClienteNome(""); setNovoClienteTelefone(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" && clienteNome.trim() && !selectedClienteId && !buscandoCliente && filteredClientes.length === 0) { e.preventDefault(); openNovoClienteModal(); } }}
                onFocus={() => setShowClienteDropdown(true)}
                onBlur={() => setTimeout(() => setShowClienteDropdown(false), 150)}
                className="w-full h-11 min-h-[44px] sm:h-9 sm:min-h-0 text-sm border-orange-400/50 dark:border-orange-500/40 focus:ring-orange-500 focus:border-orange-500 bg-orange-50/50 dark:bg-orange-500/5 pr-10"
              />
              {!selectedClienteId && !buscandoCliente && clienteNome.trim().length >= 2 && filteredClientes.length === 0 && (
                <button type="button" onClick={openNovoClienteModal} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center transition-colors" title="Cadastrar novo cliente">
                  <UserPlus className="w-3.5 h-3.5 text-white" />
                </button>
              )}
              {showClienteDropdown && !selectedClienteId && clienteNome.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredClientes.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={() => { guardarCliente(c); setClienteNome(c.nome || ""); setSelectedClienteId(c.id); setShowClienteDropdown(false); setShowCadastrarCliente(false); setShowCadastrarForm(false); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-muted focus:bg-muted text-sm touch-manipulation"
                    >
                      <span className="font-medium">{c.nome}</span>
                      {c.telefone && <span className="text-muted-foreground text-xs ml-2">{c.telefone}</span>}
                    </button>
                  ))}
                  {buscandoCliente && (
                    <p className="px-3 py-2.5 text-sm text-muted-foreground" data-e2e="cliente-buscando">Buscando…</p>
                  )}
                  {!buscandoCliente && filteredClientes.length === 0 && (
                    <button
                      type="button"
                      onMouseDown={() => { setShowClienteDropdown(false); openNovoClienteModal(); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-orange-500/10 flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400 font-medium touch-manipulation"
                    >
                      <UserPlus className="w-4 h-4 shrink-0" />
                      Cadastrar novo cliente
                    </button>
                  )}
                </div>
              )}
              {/* Dialog Cadastrar Novo Cliente */}
              <Dialog open={showCadastrarForm} onOpenChange={(open) => { if (!open) closeNovoClienteModal(); }}>
                <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-orange-500" />
                      Cadastrar Novo Cliente
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold">Nome <span className="text-red-500">*</span></Label>
                        <Input placeholder="Nome completo" value={novoClienteNome} onChange={(e) => setNovoClienteNome(e.target.value)} className="h-10 mt-1" autoFocus />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold">Telefone</Label>
                        <Input placeholder="(00) 0.0000-0000" value={novoClienteTelefone} onChange={(e) => setNovoClienteTelefone(formatPhone(e.target.value))} className="h-10 mt-1" inputMode="tel" />
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Informações extras (opcional)</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">CPF</Label>
                          <Input placeholder="000.000.000-00" value={novoClienteCpf} onChange={(e) => { const n = e.target.value.replace(/\D/g, ""); let f = n; if (n.length <= 3) f = n; else if (n.length <= 6) f = `${n.slice(0,3)}.${n.slice(3)}`; else if (n.length <= 9) f = `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6)}`; else f = `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}-${n.slice(9,11)}`; setNovoClienteCpf(f); }} className="h-9 mt-1" inputMode="numeric" />
                        </div>
                        <div>
                          <Label className="text-xs">Data de Nascimento</Label>
                          <Input type="date" value={novoClienteNascimento} onChange={(e) => setNovoClienteNascimento(e.target.value)} className="h-9 mt-1" />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleCadastrarNovoCliente} disabled={!novoClienteNome?.trim() || cadastrandoCliente} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                        {cadastrandoCliente ? "Cadastrando..." : "Cadastrar Cliente"}
                      </Button>
                      <Button variant="outline" onClick={closeNovoClienteModal}>Cancelar</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {/* Observação da mesa */}
            <div className="w-full">
              <label className="text-xs font-semibold text-orange-600 dark:text-orange-400 block mb-0.5 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                Observação
              </label>
              <Input
                placeholder="Ex: alergia a camarão, mesa do aniversariante…"
                value={mesaObservacao}
                onChange={(e) => setMesaObservacao(e.target.value)}
                maxLength={250}
                className="h-11 min-h-[44px] sm:h-9 sm:min-h-0 text-sm border-orange-400/50 dark:border-orange-500/40 focus:ring-orange-500 focus:border-orange-500 bg-orange-50/50 dark:bg-orange-500/5"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
          {/* Área de Produtos */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Busca por item — computador; no celular vive na lupa da barra de categorias */}
            <div className="hidden sm:block px-3 pt-2 pb-2 border-b bg-muted/10 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar produto..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="pl-9 pr-9 h-9 rounded-lg text-sm"
                />
                {productSearchTerm && (
                  <button onClick={() => setProductSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {/* Categorias — chips slim, igual cardápio */}
            <div className="px-3 py-2 border-b bg-muted/20 shrink-0 flex items-center gap-2">
              {/* Celular: lupa parada no canto esquerdo enquanto os chips rolam; toque abre a busca no lugar deles */}
              {buscaAberta ? (
                <div className="relative flex-1 min-w-0 sm:hidden">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-500" />
                  <Input
                    autoFocus
                    placeholder="Buscar produto..."
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    className="pl-9 pr-11 h-9 rounded-full text-sm"
                    data-e2e="mesa-busca-campo"
                  />
                  <button type="button" onClick={fecharBusca} aria-label="Fechar busca" data-e2e="mesa-busca-fechar"
                    className="absolute right-0 top-0 h-9 w-10 flex items-center justify-center text-muted-foreground touch-manipulation">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setBuscaAberta(true)} aria-label="Buscar produto" data-e2e="mesa-busca-lupa"
                  className="sm:hidden shrink-0 h-9 w-9 rounded-full border border-border bg-card flex items-center justify-center text-foreground active:scale-95 touch-manipulation">
                  <Search className="w-4 h-4" />
                </button>
              )}
              <div className={`${buscaAberta ? 'hidden sm:flex' : 'flex'} flex-1 min-w-0 items-center gap-2 overflow-x-auto scrollbar-hide`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 ${
                    selectedCategory === "all"
                      ? "bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/30"
                      : "border-border bg-card text-foreground hover:border-orange-500 hover:bg-orange-500/10 hover:text-orange-500"
                  }`}
                >
                  Todos
                </button>
                {categoriesWithProducts.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.name)}
                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 ${
                      selectedCategory === cat.name
                        ? "bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/30"
                        : "border-border bg-card text-foreground hover:border-orange-500 hover:bg-orange-500/10 hover:text-orange-500"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Produtos — grid responsivo; pb no mobile para barra fixa + safe-area; min-[360px]/[375px] para 320–414px */}
            <div
              className="flex-1 overflow-auto overflow-x-hidden p-1.5 min-[360px]:p-2 sm:p-3 md:p-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:pb-4 min-h-0"
              onScroll={(e) => { if (e.target.scrollTop > 40) setMesaInfoExpanded(prev => prev ? false : prev); }}
            >
              {loading ? (
                <CarregandoGrade comImagem quantos={10} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2 sm:gap-3" />
              ) : (
                <div className="space-y-4 sm:space-y-5 md:space-y-6">
                  {/* Promoções Ativas no Topo */}
                  {!buscando && activePromotions.length > 0 && activePromotions.map(promo => {
                    const promoProds = (promo.products || [])
                      .map(pp => (allProducts.length ? allProducts : products).find(p => p.id === pp.product_id))
                      .filter(Boolean);
                    if (promoProds.length === 0) return null;
                    return (
                      <div key={promo.id} className="min-w-0">
                        <div className="flex items-center justify-between mb-1.5 sm:mb-2 md:mb-3">
                          <h2 className="text-[10px] min-[360px]:text-xs sm:text-sm md:text-base lg:text-lg font-bold text-green-500 uppercase truncate flex items-center gap-1">
                            <span>🔥</span> {promo.name}
                          </h2>
                        </div>
                        {promo.description && <p className="text-[10px] text-muted-foreground mb-1.5 -mt-1">{promo.description}</p>}
                        {/* Celular: carrossel igual ao das categorias — a grade de 2 colunas
                            com foto alta ocupava a tela inteira (auditoria mobile, 25/09/2026) */}
                        <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 sm:pb-0 sm:overflow-visible sm:snap-none sm:grid sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-1.5 sm:gap-2 lg:gap-2.5">
                          {promoProds.map(product => {
                            const isUnavailable = product.available === false;
                            return (
                              <button key={product.id} onClick={() => !isUnavailable && openProductPopup(product)} className={`bg-card rounded-xl overflow-hidden border-2 border-green-500/40 text-left transition-all shadow-sm snap-start flex-shrink-0 w-[140px] sm:w-auto ${isUnavailable ? "opacity-60 cursor-not-allowed" : "hover:border-green-500/60 hover:shadow-md active:scale-[0.98]"}`}>
                                <div className={`aspect-square sm:aspect-[9/11.5] lg:aspect-[3.6/3.9675] relative overflow-hidden ${
                                  product.photo_url && isPngImage(product.photo_url)
                                    ? isDark ? 'bg-gradient-to-b from-orange-900/20 to-orange-800/30' : 'bg-gradient-to-b from-orange-50 to-orange-100'
                                    : 'bg-muted'
                                }`}>
                                  {product.photo_url ? (
                                    <img src={miniaturaDe(product.photo_url, 400)} alt="" className={`w-full h-full ${isPngImage(product.photo_url) ? 'object-contain p-0.5' : 'object-cover'} ${isUnavailable ? 'grayscale' : ''}`} loading="lazy" decoding="async" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center"><span className="text-lg lg:text-xl">🍽️</span></div>
                                  )}
                                  {isUnavailable && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                      <span className="bg-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">ESGOTADO</span>
                                    </div>
                                  )}
                                  {!isUnavailable && (() => {
                                    const pi = getPromoForProduct(product.id, 'simples') || getPromoForProduct(product.id, 'combo');
                                    if (!pi || pi.value == null) return null;
                                    const label = pi.type === 'percentage' ? `-${Math.round(pi.value)}%` : `R$ ${Number(pi.value).toFixed(2).replace('.', ',')}`;
                                    return (
                                      <div className="absolute top-0 left-0 z-10">
                                        <div className="bg-green-500 text-white font-bold text-[9px] lg:text-[10px] px-1.5 py-0.5 rounded-br-lg shadow-md flex items-center gap-0.5">
                                          <span>🔥</span> {label}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div className="p-1.5 lg:p-2">
                                  <h3 className="font-semibold text-foreground text-[11px] lg:text-xs line-clamp-1 lg:line-clamp-2 leading-tight mb-0.5">{product.name}</h3>
                                  {(() => {
                                    const promoSimple = getPromoForProduct(product.id, 'simples');
                                    const promoCombo = getPromoForProduct(product.id, 'combo');
                                    const simpleOrig = product.simple_price || Math.round((product.sale_price || 0) * 0.7 * 100) / 100;
                                    const comboOrig = product.sale_price || 0;
                                    const simpleFinal = calcPromoPrice(simpleOrig, promoSimple);
                                    const comboFinal = calcPromoPrice(comboOrig, promoCombo);

                                    if (product.product_type === 'combo') {
                                      return (
                                        <div className={`flex justify-center items-start gap-2 mt-0.5 ${isUnavailable ? 'opacity-50' : ''}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-[8px] lg:text-[9px] font-medium text-muted-foreground uppercase tracking-wide">Individual</span>
                                            {simpleFinal != null && simpleFinal !== simpleOrig ? (
                                              <>
                                                <span className="text-[9px] line-through text-muted-foreground">R$ {simpleOrig.toFixed(2).replace('.', ',')}</span>
                                                <span className="font-bold text-[11px] lg:text-xs text-green-500">R$ {simpleFinal.toFixed(2).replace('.', ',')}</span>
                                              </>
                                            ) : (
                                              <span className={`font-bold text-[11px] lg:text-xs ${isUnavailable ? 'text-muted-foreground line-through' : 'text-orange-500'}`}>
                                                R$ {simpleOrig.toFixed(2).replace('.', ',')}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex flex-col items-center">
                                            <span className="text-[8px] lg:text-[9px] font-medium text-muted-foreground uppercase tracking-wide">Combo</span>
                                            {comboFinal != null && comboFinal !== comboOrig ? (
                                              <>
                                                <span className="text-[9px] line-through text-muted-foreground">R$ {comboOrig.toFixed(2).replace('.', ',')}</span>
                                                <span className="font-bold text-[11px] lg:text-xs text-green-500">R$ {comboFinal.toFixed(2).replace('.', ',')}</span>
                                              </>
                                            ) : (
                                              <span className={`font-bold text-[11px] lg:text-xs ${isUnavailable ? 'text-muted-foreground line-through' : 'text-orange-500'}`}>
                                                R$ {comboOrig.toFixed(2).replace('.', ',')}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      const origPrice = product.sale_price || 0;
                                      const finalPrice = calcPromoPrice(origPrice, promoSimple);
                                      return finalPrice != null && finalPrice !== origPrice ? (
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                          <span className="text-[10px] line-through text-muted-foreground">R$ {origPrice.toFixed(2).replace('.', ',')}</span>
                                          <span className="font-bold text-[11px] lg:text-xs text-green-500">R$ {finalPrice.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                      ) : (
                                        <span className={`font-bold text-[11px] lg:text-xs ${isUnavailable ? 'text-muted-foreground line-through' : 'text-orange-500'}`}>
                                          R$ {origPrice.toFixed(2).replace('.', ',')}
                                        </span>
                                      );
                                    }
                                  })()}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {buscando && sectionsToShowFiltered.length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-10" data-e2e="mesa-busca-vazia">
                      Nenhum produto com “{productSearchTerm.trim()}”
                    </p>
                  )}
                  {sectionsToShowFiltered.map(([category, categoryProducts]) => (
                    <div key={category} className="min-w-0">
                      {/* Header categoria — igual Delivery */}
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-base lg:text-lg font-bold text-orange-500 uppercase">{category}</h2>
                        <span className="text-xs text-muted-foreground">{categoryProducts.length} {categoryProducts.length === 1 ? 'item' : 'itens'}</span>
                      </div>
                      {/* Carrossel mobile / Grid desktop — formato Delivery */}
                      <div className="flex lg:grid gap-3 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 snap-x snap-mandatory lg:snap-none scrollbar-hide lg:grid-cols-[repeat(auto-fill,minmax(170px,1fr))]">
                        {[...categoryProducts]
                          .sort((a, b) => (a.available === false ? 1 : 0) - (b.available === false ? 1 : 0))
                          .map((product) => {
                          const isUnavailable = product.available === false;
                          const isComboType = product.product_type === 'combo';
                          const simplePrice = product.simple_price || 0;
                          const comboPrice = product.sale_price || 0;
                          const promoSimples = getPromoForProduct(product.id, 'simples');
                          const promoCombo = getPromoForProduct(product.id, 'combo');
                          const hasPromo = !isUnavailable && (promoSimples || promoCombo);
                          const simpleFinal = calcPromoPrice(isComboType ? simplePrice : comboPrice, promoSimples);
                          const comboFinal = calcPromoPrice(comboPrice, promoCombo);
                          const showBothPrices = isComboType && simplePrice > 0 && comboPrice > 0 && simplePrice !== comboPrice;
                          const lowestPromo = [promoSimples, promoCombo].filter(Boolean)[0];
                          const unitSuffix = (product.sale_unit && product.sale_unit !== "un")
                            ? <span className="text-[10px] font-normal text-muted-foreground ml-0.5">/{product.sale_unit}</span>
                            : null;
                          return (
                            <div
                              key={product.id}
                              onClick={() => !isUnavailable && openProductPopup(product)}
                              className={`relative group bg-card rounded-xl overflow-hidden snap-start flex-shrink-0 w-[160px] lg:w-auto flex flex-col transition-all cursor-pointer border ${
                                isUnavailable
                                  ? 'opacity-60 cursor-not-allowed border-border'
                                  : hasPromo
                                    ? 'border-green-500/60 shadow-[0_0_0_1px_rgba(34,197,94,0.3)] hover:shadow-lg'
                                    : 'border-border hover:border-orange-500/40 hover:shadow-md'
                              }`}
                            >
                              <div className="aspect-square relative overflow-hidden bg-muted">
                                {product.photo_url ? (
                                  <img
                                    src={miniaturaDe(product.photo_url, 400)}
                                    alt={product.name}
                                    className={`w-full h-full ${isPngImage(product.photo_url) ? 'object-contain p-1' : 'object-cover'} group-hover:scale-105 transition-transform duration-300 ${isUnavailable ? 'grayscale' : ''}`}
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                                    <ImageOff className="w-10 h-10" strokeWidth={1.5} />
                                  </div>
                                )}
                                {isUnavailable && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">ESGOTADO</span>
                                  </div>
                                )}
                                {lowestPromo && lowestPromo.value != null && !isUnavailable && (
                                  <div className="absolute top-1.5 left-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-extrabold text-[10px] px-2 py-0.5 rounded-md shadow-sm">
                                    {lowestPromo.type === 'percentage'
                                      ? `-${Math.round(lowestPromo.value)}%`
                                      : `- R$ ${Number(lowestPromo.value).toFixed(2).replace('.', ',')}`}
                                  </div>
                                )}
                                {isComboType && !isUnavailable && (
                                  <div className="absolute top-1.5 right-1.5 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow-sm tracking-wider">
                                    COMBO
                                  </div>
                                )}
                              </div>
                              <div className="p-2.5 flex flex-col flex-1">
                                <h3 className="font-semibold text-foreground text-sm leading-tight line-clamp-2 mb-0.5">
                                  {product.name}
                                </h3>
                                {product.description && (
                                  <p className="text-muted-foreground text-[11px] line-clamp-2 mb-2 leading-snug">
                                    {product.description}
                                  </p>
                                )}
                                <div className="mt-auto pt-1.5 border-t border-border/50 space-y-0.5">
                                  {showBothPrices ? (
                                    <>
                                      <div className="flex items-center justify-between gap-1">
                                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Simples</span>
                                        {simpleFinal != null && simpleFinal !== simplePrice && !isUnavailable ? (
                                          <span className="flex items-center gap-1">
                                            <span className="text-[9px] line-through text-muted-foreground/70">R$ {simplePrice.toFixed(2).replace('.', ',')}</span>
                                            <span className="font-bold text-sm text-green-600 dark:text-green-500">R$ {simpleFinal.toFixed(2).replace('.', ',')}</span>
                                          </span>
                                        ) : (
                                          <span className={`font-bold text-sm ${isUnavailable ? 'text-muted-foreground line-through' : 'text-foreground'}`}>R$ {simplePrice.toFixed(2).replace('.', ',')}</span>
                                        )}
                                      </div>
                                      <div className="flex items-center justify-between gap-1">
                                        <span className="text-[10px] text-orange-500 uppercase tracking-wider font-bold">Combo</span>
                                        {comboFinal != null && comboFinal !== comboPrice && !isUnavailable ? (
                                          <span className="flex items-center gap-1">
                                            <span className="text-[9px] line-through text-muted-foreground/70">R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                                            <span className="font-bold text-sm text-green-600 dark:text-green-500">R$ {comboFinal.toFixed(2).replace('.', ',')}</span>
                                          </span>
                                        ) : (
                                          <span className={`font-bold text-sm ${isUnavailable ? 'text-muted-foreground line-through' : 'text-orange-500'}`}>R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    (() => {
                                      const op = comboPrice;
                                      const fp = simpleFinal;
                                      return fp != null && fp !== op && !isUnavailable ? (
                                        <div className="flex items-baseline justify-between">
                                          <span className="text-[10px] line-through text-muted-foreground/70">R$ {op.toFixed(2).replace('.', ',')}</span>
                                          <span className="font-bold text-base text-green-600 dark:text-green-500">R$ {fp.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                        </div>
                                      ) : (
                                        <span className={`block text-right font-bold text-base ${isUnavailable ? 'text-muted-foreground line-through' : 'text-orange-500'}`}>R$ {op.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                      );
                                    })()
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Carrinho da Mesa — sidebar: Novos itens (em cima) e Enviados anteriormente (separação visual) */}
          <div className="hidden lg:flex w-80 flex-col border-l bg-muted/20 shrink-0">
            <div className="p-4 border-b">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                <h3 className="font-bold">Pedido</h3>
                {cart.length > 0 && <span className="text-sm text-muted-foreground">({cartItemsCount} novos)</span>}
              </div>
            </div>

            <div className="flex-1 overflow-auto p-3 space-y-4">
              {/* Novos itens (a enviar) — em cima */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Novos itens (a enviar)</p>
                <AvisoOfertasCliente ofertasDia={ofertasCliente} cart={cart} className="mb-2" />
                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum item</p>
                ) : (
                <div className="space-y-2">
                  {cart.map((item) => (
                    <div key={item.cartItemId || item.id} className="space-y-1.5">
                      <CartItemCard
                        item={item}
                        allProducts={allProducts}
                        onIncrement={() => updateQuantity(item.cartItemId || item.id, 1)}
                        onDecrement={() => updateQuantity(item.cartItemId || item.id, -1)}
                        onRemove={() => removeFromCart(item.cartItemId || item.id)}
                        onEdit={() => handleEditCartItem(item)}
                      />
                      <DonoDoItem donos={donos} item={item} />
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Enviados anteriormente — separação visual (abaixo dos novos). Itens cancelados são ocultados. */}
              {((selectedMesa?.items) || []).filter(i => !i.deleted).length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Enviados anteriormente</p>
                  <div className="space-y-2">
                    {(selectedMesa?.items || [])
                      .map((item, idx) => ({ item, idx }))
                      .filter(({ item }) => !item.deleted)
                      .map(({ item, idx }) => {
                      const unitPrice = item.final_price ?? item.sale_price ?? 0;
                      const posicao = (selectedMesa?.items || []).filter(i => !i.deleted).indexOf(item);
                      const pago = pagoDoItem(contaComanda, item, posicao);
                      // Quilo/litro pelo cadastro (igual à lista do celular): "1,250 kg ·"
                      const medida = medidaDoItem(item, allProducts);
                      return (
                        <div
                          key={item.cartItemId || item.id || `env-${idx}`}
                          className={`flex items-center gap-2 p-2 rounded-lg relative ${pago?.quitado ? "bg-emerald-500/5 ring-1 ring-emerald-500/30" : "bg-muted/50"}`}
                        >
                          <div className="w-10 h-10 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                            {item.photo_url ? <img src={miniaturaDe(item.photo_url, 128)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-base">🍽️</div>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {item.name || item.nome}
                            </p>
                            <p className={`text-xs text-muted-foreground ${pago?.quitado ? "line-through" : ""}`}>
                              {medida ? `${formatarQtd(item.quantity || 1, medida)} ·` : `${item.quantity || 1}×`} R$ {(unitPrice * (item.quantity || 1)).toFixed(2).replace('.', ',')}
                            </p>
                            <SeloPagoItem pago={pago} className="mr-2" />
                            <SeloItemMesa situacao={situacaoDe(item, selectedMesa?.items)} />
                            <QuemPediu className="mt-1" fotoDe={getImageUrl}
                              info={quemPediu(item, donos, posicao)} />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 flex-shrink-0 text-red-500 hover:bg-red-100 ${pago ? "opacity-30" : ""}`}
                            onClick={() => (pago ? toggleItemDeleted(idx) : setItemParaCancelar({ idx, item }))}
                            title={pago ? "Já foi pago — apague o pagamento no Fechar mesa antes" : "Deletar item"}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <ItensCancelados itens={selectedMesa?.items} produtos={allProducts} />
            </div>

            {/* Total, ENVIAR e Fechar mesa */}
            <div className="p-4 border-t bg-background">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Total (novos)</span>
                <span className="text-xl font-bold text-primary">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
              </div>
              {(selectedMesa?.items?.length > 0 || cart.length > 0) && (
                <ResumoContaMesa className="mb-3" total={contaComanda.total + cartTotal} pago={contaComanda.pago} />
              )}
              <Button
                onClick={requestEnvio}
                className="w-full h-12 font-semibold bg-orange-500 hover:bg-orange-600"
              >
                ENVIAR
              </Button>
              {/* Fechamento só com itens LANÇADOS (v1.5.469): mesa recém-aberta
                  sem nada enviado não tem o que fechar — o botão confundia. */}
              {((selectedMesa?.items) || []).filter(i => !i.deleted).length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleFecharMesa}
                  className="w-full h-11 mt-2 font-medium"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Fechamento
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Barra fixa mobile: itens ja na mesa + resumo do carrinho + ENVIAR */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t shadow-lg pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))] pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]">
          {/* Linha 1: ja na mesa (so se houver itens enviados) — clica e abre o sheet */}
          {mesaItemsCount > 0 && (
            <button
              type="button"
              onClick={() => setShowMobileCart(true)}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 mb-2 rounded-lg bg-green-500/10 dark:bg-green-500/15 border border-green-500/30 active:opacity-80 touch-manipulation"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Receipt className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                <span className="text-xs font-semibold text-green-700 dark:text-green-300 truncate">
                  Já na mesa: {mesaItemsCount} {mesaItemsCount === 1 ? "item" : "itens"}
                </span>
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <span className="text-xs font-bold text-green-700 dark:text-green-300 tabular-nums">R$ {mesaItemsTotal.toFixed(2).replace('.', ',')}</span>
                <ChevronRight className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
              </span>
            </button>
          )}
          {/* Linha 2: carrinho atual (novos itens) + ENVIAR */}
          <div className="flex items-center justify-between gap-2 min-[360px]:gap-3 min-h-[48px]">
            <button
              type="button"
              onClick={() => setShowMobileCart(true)}
              className="flex items-center gap-2 min-w-0 flex-1 min-h-[48px] touch-manipulation rounded-lg active:opacity-80"
            >
              <ShoppingCart className="w-5 h-5 text-primary shrink-0" />
              <span className="text-xs min-[360px]:text-sm font-medium truncate">
                {cartItemsCount} {mesaItemsCount > 0 ? "novos" : "itens"}
              </span>
              <span className="text-xs min-[360px]:text-sm font-bold text-primary truncate">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
            </button>
            <Button
              onClick={requestEnvio}
              disabled={enviandoPedido}
              className="h-12 min-h-[48px] px-3 min-[360px]:px-4 font-semibold bg-orange-500 hover:bg-orange-600 shrink-0 touch-manipulation"
            >
              {enviandoPedido ? "ENVIANDO…" : "ENVIAR"}
            </Button>
          </div>
        </div>

        {/* Sheet do carrinho (mobile) — Novos itens (em cima) e Enviados anteriormente */}
        <Sheet open={showMobileCart} onOpenChange={setShowMobileCart}>
          <SheetContent side="bottom" className="max-h-[85dvh] flex flex-col p-0 gap-0 rounded-t-2xl">
            <div className="px-3 min-[360px]:px-4 py-3 border-b flex items-center justify-between shrink-0">
              <h3 className="font-bold text-lg">Pedido{cart.length > 0 ? ` (${cartItemsCount} novos)` : ""}</h3>
            </div>
            <div className="flex-1 overflow-auto p-3 min-[360px]:p-4 min-h-0 space-y-4">
              {/* Novos itens (a enviar) — em cima */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Novos itens (a enviar)</p>
                <AvisoOfertasCliente ofertasDia={ofertasCliente} cart={cart} className="mb-2" />
                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum item</p>
                ) : (
                <div className="space-y-2">
                  {cart.map((item) => (
                    <div key={item.cartItemId || item.id} className="space-y-1.5">
                      <CartItemCard
                        item={item}
                        allProducts={allProducts}
                        onIncrement={() => updateQuantity(item.cartItemId || item.id, 1)}
                        onDecrement={() => updateQuantity(item.cartItemId || item.id, -1)}
                        onRemove={() => removeFromCart(item.cartItemId || item.id)}
                        onEdit={() => handleEditCartItem(item)}
                      />
                      <DonoDoItem donos={donos} item={item} />
                    </div>
                  ))}
                </div>
                )}
              </div>
              {/* Enviados anteriormente — itens cancelados são ocultados */}
              {((selectedMesa?.items) || []).filter(i => !i.deleted).length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Enviados anteriormente</p>
                  <div className="space-y-2">
                    {(selectedMesa?.items || [])
                      .map((item, original) => ({ item, original }))
                      .filter(({ item }) => !item.deleted)
                      .map(({ item, original }, idx) => {
                      const unitPrice = item.final_price ?? item.sale_price ?? 0;
                      const pago = pagoDoItem(contaComanda, item, idx);
                      const medida = medidaDoItem(item, allProducts); // "1,250 kg ·" no lugar de "1.25×"
                      return (
                        <div key={item.cartItemId || item.id || `env-m-${idx}`} className={`flex items-center gap-2 p-2 min-[360px]:p-2.5 rounded-lg ${pago?.quitado ? "bg-emerald-500/5 ring-1 ring-emerald-500/30" : "bg-muted/30"}`}>
                          <div className="w-10 h-10 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                            {item.photo_url ? <img src={miniaturaDe(item.photo_url, 128)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-base">🍽️</div>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.name || item.nome}</p>
                            <p className={`text-xs text-muted-foreground ${pago?.quitado ? "line-through" : ""}`}>{medida ? `${formatarQtd(item.quantity || 1, medida)} ·` : `${item.quantity || 1}×`} R$ {(unitPrice * (item.quantity || 1)).toFixed(2).replace('.', ',')}</p>
                            <SeloPagoItem pago={pago} className="mr-2" />
                            <SeloItemMesa situacao={situacaoDe(item, selectedMesa?.items)} />
                            <QuemPediu className="mt-1" fotoDe={getImageUrl} info={quemPediu(item, donos, idx)} />
                          </div>
                          {/* Cancelar produto já enviado também no celular (dono, 25/09/2026) —
                              a mesma lixeira do computador: grava na hora, e produto pago não sai */}
                          <Button
                            variant="outline"
                            size="icon"
                            className={`h-10 w-10 flex-shrink-0 text-red-500 border-red-500/40 hover:bg-red-500/10 touch-manipulation ${pago ? "opacity-30" : ""}`}
                            onClick={() => (pago ? toggleItemDeleted(original) : setItemParaCancelar({ idx: original, item }))}
                            title={pago ? "Já foi pago — apague o pagamento no Fechar mesa antes" : "Deletar item"}
                            aria-label={`Deletar ${item.name || item.nome}`}
                            data-e2e="mesa-enviado-lixeira"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <ItensCancelados itens={selectedMesa?.items} produtos={allProducts} />
            </div>
            <div className="px-3 min-[360px]:px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] border-t bg-background shrink-0">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Total (novos)</span>
                <span className="text-xl font-bold text-primary">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
              </div>
              {(selectedMesa?.items?.length > 0 || cart.length > 0) && (
                <ResumoContaMesa className="mb-3" total={contaComanda.total + cartTotal} pago={contaComanda.pago} />
              )}
              <Button onClick={() => { setShowMobileCart(false); requestEnvio(); }} className="w-full h-12 min-h-[48px] font-semibold bg-orange-500 hover:bg-orange-600 touch-manipulation">
                ENVIAR
              </Button>
              {/* Fechamento só com itens LANÇADOS (v1.5.469): mesa recém-aberta
                  sem nada enviado não tem o que fechar — o botão confundia. */}
              {((selectedMesa?.items) || []).filter(i => !i.deleted).length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => { handleFecharMesa(); setShowMobileCart(false); }}
                  className="w-full h-12 min-h-[48px] mt-2 font-medium touch-manipulation"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Fechamento
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Popup de Produto — mesma lógica do Cardápio: combo/simples, etapas, resumo, preços automáticos */}
        <ProductPopupCardapio
          product={selectedProduct}
          open={productPopupOpen}
          onClose={closeProductPopup}
          onAddToCart={handleAddToCartFromPopup}
          darkMode={isDark}
          allProducts={allProducts.length ? allProducts : products}
          activePromotions={activePromotions}
          initialData={editingInitial}
          modulo="Mesas"
        />
        {/* Popup dedicado de Pizza (montagem com tamanhos/sabores/borda/adicionais) */}
        {pizza.popup}

        {/* Dialog de Conferência antes de Enviar */}
        <Dialog open={confirmEnvioOpen} onOpenChange={setConfirmEnvioOpen}>
          {/* Enter envia, Esc volta (dono, 26/10/2026) */}
          <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-md rounded-xl max-h-[85dvh] flex flex-col p-0 gap-0" data-e2e="mesa-conferencia"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.repeat) { e.preventDefault(); confirmarEnvio(); } }}>
            <DialogHeader className="px-5 py-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                Conferência do pedido
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Confira os {cart.length} {cart.length === 1 ? 'item' : 'itens'} antes de enviar para a cozinha
              </p>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2 min-h-0">
              {cart.length > 0 && (
                <DesativarTodosPreparo
                  chaves={cart.map((i) => i.cartItemId || i.id)}
                  desligados={naoImprimirPreparo}
                  onMudar={setNaoImprimirPreparo}
                />
              )}
              {cart.map((item, idx) => {
                const chave = item.cartItemId || item.id;
                const imprime = !naoImprimirPreparo.has(chave);
                return (
                  /* Toggle de impressão do PREPARO (v1.5.514): botão-coluna à
                     esquerda do card, só o ícone da impressora — sem texto
                     (pedido do dono). Ligado = azul; desligado = vermelho com o
                     ícone cortado, card esmaecido e selo "NÃO IMPRIME". Por
                     padrão TODOS ligados; desligar tira o item só do cupom de
                     preparo — o pedido enviado continua completo. */
                  <div key={item.cartItemId || `${item.id}-${idx}`} className="flex items-stretch gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setNaoImprimirPreparo((prev) => {
                          const novo = new Set(prev);
                          if (novo.has(chave)) novo.delete(chave);
                          else novo.add(chave);
                          return novo;
                        });
                      }}
                      title={imprime
                        ? "Sai no cupom de preparo — clique para NÃO imprimir"
                        : "NÃO sai no cupom de preparo — clique para voltar a imprimir"}
                      className={`w-10 shrink-0 rounded-lg border flex items-center justify-center transition-colors ${
                        imprime
                          ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                          : "border-red-500/50 bg-red-500/10 text-red-500 hover:bg-red-500/20"
                      }`}
                    >
                      <span className="relative inline-flex">
                        <Printer className="w-4 h-4" />
                        {!imprime && (
                          <span className="absolute left-1/2 top-1/2 w-6 h-[2px] bg-red-500 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full" />
                        )}
                      </span>
                    </button>
                    <div className={`flex-1 relative min-w-0 transition-opacity ${imprime ? "" : "opacity-50"}`}>
                      <CartItemCard item={item} allProducts={allProducts} readonly />
                      {!imprime && (
                        <span className="absolute top-1.5 right-1.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500 text-white shadow">
                          não imprime
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t bg-muted/30 px-5 py-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-xl font-bold text-orange-500">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setConfirmEnvioOpen(false)} className="h-11" disabled={enviandoPedido}>
                  Voltar
                </Button>
                <Button
                  className="h-11 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                  disabled={enviandoPedido}
                  data-e2e="mesa-conferencia-enviar"
                  onClick={confirmarEnvio}
                >
                  {enviandoPedido ? "Enviando…" : "Enviar"}
                  <kbd className="hidden sm:inline ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/20">Enter</kbd>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cálculo de troco do DINHEIRO (v1.5.518) */}
      <PixGerarQrModal
        open={showPixConfirm}
        onOpenChange={(v) => { setShowPixConfirm(v); if (!v) setSelectedPayment((cur) => (/pix_online/i.test(cur || "") ? null : cur)); }}
        total={totalParaFechar}
        onGerar={prepararPixOnline}
      />
      <TrocoModal
        open={showTrocoModal}
        onOpenChange={setShowTrocoModal}
        total={totalParaFechar}
        onConfirm={setCashReceived}
      />

      {/* Pagamento do Fechar mesa — features/mesas-fechamento */}
      <FechamentoMesaDialog f={fechamentoProps} />

        {/* PIX Online — cobrança avulsa do saldo da mesa */}
        <PixCobrancaModal
          open={showPixModal}
          onClose={() => { setShowPixModal(false); setPixOrderId(null); setPaymentDialogOpen(true); }}
          pedidoId={pixOrderId}
          amount={pixValor}
          nomeCliente={selectedMesa?.cliente_nome || ""}
          onPago={handlePixConfirmed}
        />
      </div>
    );
  }

  // Tela principal de Mesas
  return (
    <div className="p-3 sm:p-6 min-h-[100dvh] pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] overflow-x-hidden">
      {/* Header — título à esquerda, ação 'Nova Mesa' slim no canto sup. direito.
          Mantemos slim pra reforçar visualmente que abrir mesa NÃO é o caminho aqui
          (o usuário deve clicar no card da mesa fechada). */}
      <div className="mb-3 sm:mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl md:text-3xl font-bold truncate">Mesas</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Mesa QR: conferir o comprovante do cliente e liberar a saída */}
          {saidaQrLigada && <Button
            onClick={() => setLendoSaida(true)}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs gap-1"
            data-e2e="mesas-ler-saida"
          >
            <ScanLine className="w-3.5 h-3.5" strokeWidth={1.8} />
            Ler QR de saída
          </Button>}
          <Button
            onClick={() => setQrEmMassa(true)}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs gap-1"
            title="Baixar ou imprimir o QR Code de todas as mesas"
            data-e2e="mesas-qr-massa"
          >
            <QrCode className="w-3.5 h-3.5" strokeWidth={1.8} />
            <span className="hidden sm:inline">QR Codes</span>
          </Button>
          <Button
            onClick={handleCreateMesa}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1"
            title="Cadastrar uma nova mesa no sistema (não é abrir mesa)"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
            Nova mesa
          </Button>
        </div>
      </div>
      {/* Loja que exige caixa aberto, com ele fechado: o QR recusa todo cliente */}
      <FaixaQrSemCaixa visivel={qrSemCaixa} onAbrirCaixa={onAbrirCaixa} />
      <LerQrSaida aberto={lendoSaida} onFechar={() => setLendoSaida(false)} />
      {/* QR Code de uma mesa (três pontinhos do card) e de todas (botão
          "QR Codes" do topo): baixar imagem/PDF e imprimir na impressora. */}
      <DialogQrMesa
        mesaId={mesaQrSelecionada}
        onFechar={() => setMesaQrSelecionada(null)}
        onAbrirTodas={() => { setMesaQrSelecionada(null); setQrEmMassa(true); }}
      />
      <DialogQrCodesEmMassa aberto={qrEmMassa} onFechar={() => setQrEmMassa(false)} />
      {dialogoNomeQr}

      {/* Barra de Pesquisa centralizada */}
      <div className="flex justify-center mb-3 sm:mb-6">
        <div className="relative w-full max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por número, comanda ou cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 sm:h-10 min-h-[44px] sm:min-h-0 rounded-full"
          />
        </div>
      </div>

      {primeiraCarga ? <MesasCarregando /> : (<>
      {/* Mesas em Fechamento (Aguardando Pagamento) — sempre no topo */}
      {mesasFechamento.length > 0 && (
        <div className="mb-5 sm:mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse"></div>
            Aguardando Pagamento ({mesasFechamento.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(130px,200px))] gap-1.5 sm:gap-2 md:gap-4">
            {mesasFechamento.map((mesa) => (
              <div key={mesa.id} data-mesa-nav={mesa.id}
                className={`rounded-xl transition-shadow ${String(mesaFoco) === String(mesa.id) ? "ring-4 ring-primary ring-offset-2 ring-offset-background" : ""}`}>
              <MesaCard
                mesa={mesa}
                onClick={handleMesaClick}
                onEdit={handleEditMesa}
                onQrCode={(m) => setMesaQrSelecionada(m.id)}
                onDelete={handleDeleteMesa}
                onCancelarMesa={handleAbrirCancelarMesa}
                onLiberarMesa={handleLiberarMesa}
                onFecharMesa={handleFecharMesaDireta}
                onFecharSemImprimir={handleFecharMesaSemImprimir}
                onConfirmarPagamento={handleConfirmarPagamento}
                onReabrirMesa={handleReabrirMesa}
                onTransferirMesa={handleAbrirTransferir}
                onImprimirAmbos={handleImprimirAmbos}
                onImprimirFechamento={handleImprimirFechamento}
                onImprimirPreparo={handleImprimirPreparo}
                isMobile={isMobile}
              />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mesas Abertas */}
      {mesasAbertas.length > 0 && (
        <div className="mb-5 sm:mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            Mesas Abertas ({mesasAbertas.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(130px,200px))] gap-1.5 sm:gap-2 md:gap-4">
            {mesasAbertas.map((mesa) => (
              <div key={mesa.id} data-mesa-nav={mesa.id}
                className={`rounded-xl transition-shadow ${String(mesaFoco) === String(mesa.id) ? "ring-4 ring-primary ring-offset-2 ring-offset-background" : ""}`}>
              <MesaCard
                mesa={mesa}
                produtos={allProducts}
                onClick={handleMesaClick}
                onVerComanda={setComandaMesa}
                onEdit={handleEditMesa}
                onQrCode={(m) => setMesaQrSelecionada(m.id)}
                onDelete={handleDeleteMesa}
                onCancelarMesa={handleAbrirCancelarMesa}
                onLiberarMesa={handleLiberarMesa}
                onFecharMesa={handleFecharMesaDireta}
                onFecharSemImprimir={handleFecharMesaSemImprimir}
                onConfirmarPagamento={handleConfirmarPagamento}
                onReabrirMesa={handleReabrirMesa}
                onTransferirMesa={handleAbrirTransferir}
                onImprimirAmbos={handleImprimirAmbos}
                onImprimirFechamento={handleImprimirFechamento}
                onImprimirPreparo={handleImprimirPreparo}
                isMobile={isMobile}
              />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mesas Fechadas — cards bem próximos, gap máx 6px, sem margens nem padding extra no container */}
      <div className="p-0">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-muted-foreground"></div>
          Mesas Disponíveis ({mesasFechadas.length})
        </h2>
        {mesasFechadas.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {/* Com a busca preenchida, "todas ocupadas" mentia: só nenhuma livre casou */}
            <p>{searchTerm ? `Nenhuma mesa livre com “${searchTerm}”` : "Todas as mesas estão ocupadas"}</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(73px,112px))] gap-1.5 sm:gap-2 md:gap-3">
            {mesasFechadas.map((mesa) => (
              <div key={mesa.id} data-mesa-nav={mesa.id}
                className={`rounded-xl transition-shadow ${String(mesaFoco) === String(mesa.id) ? "ring-4 ring-primary ring-offset-2 ring-offset-background" : ""}`}>
              <MesaCard
                mesa={mesa}
                onClick={handleMesaClick}
                onEdit={handleEditMesa}
                onQrCode={(m) => setMesaQrSelecionada(m.id)}
                onDelete={handleDeleteMesa}
                onCancelarMesa={handleAbrirCancelarMesa}
                onLiberarMesa={handleLiberarMesa}
                onFecharMesa={handleFecharMesaDireta}
                onFecharSemImprimir={handleFecharMesaSemImprimir}
                onConfirmarPagamento={handleConfirmarPagamento}
                onReabrirMesa={handleReabrirMesa}
                onTransferirMesa={handleAbrirTransferir}
                onImprimirAmbos={handleImprimirAmbos}
                onImprimirFechamento={handleImprimirFechamento}
                onImprimirPreparo={handleImprimirPreparo}
                isMobile={isMobile}
              />
              </div>
            ))}
          </div>
        )}
      </div>
      </>)}

      {/* Comanda da mesa (olhinho do cartão) */}
      <ComandaMesaDialog
        mesa={comandaMesa}
        onClose={() => setComandaMesa(null)}
        onAdicionar={(m) => {
          setComandaMesa(null);
          handleMesaClick(mesas.find((x) => x.id === m.id) || m);
        }}
        allProducts={allProducts}
        fotoDe={(u) => miniaturaDe(u, 128)}
      />

      {/* Dialog Criar/Editar Mesa */}
      <Dialog open={mesaDialogOpen} onOpenChange={setMesaDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? "Editar Mesa" : "Nova Mesa"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Número da Mesa</Label>
              <Input
                type="number"
                value={mesaNumero}
                onChange={(e) => setMesaNumero(e.target.value)}
                placeholder="Ex: 1"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Nome (opcional)</Label>
              <Input
                value={mesaNome}
                onChange={(e) => setMesaNome(e.target.value)}
                placeholder="Ex: Mesa VIP, Varanda..."
                className="mt-1"
              />
            </div>
            <Button onClick={handleSaveMesa} className="w-full">
              {editMode ? "Salvar" : "Criar Mesa"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Delete */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-h-[90dvh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar Mesa?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar a mesa {mesaToDelete?.numero}? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Cancelar Mesa (mesma lógica do Delivery) — pedidos ficam em Pedidos com status CANCELADO */}
      <Dialog open={cancelarMesaModalOpen} onOpenChange={setCancelarMesaModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancelar Mesa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {mesaParaCancelar && (
              <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="font-semibold">Mesa {String(mesaParaCancelar.numero).padStart(2, "0")}</p>
                <p className="text-sm text-muted-foreground">{mesaParaCancelar.nome || `Mesa ${mesaParaCancelar.numero}`}</p>
                <p className="text-lg font-bold text-primary mt-1">R$ {(mesaParaCancelar.total || 0).toFixed(2).replace('.', ',')}</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-semibold">Motivo do Cancelamento <span className="text-red-500">*</span></Label>
              <Textarea
                value={motivoCancelamentoMesa}
                onChange={(e) => setMotivoCancelamentoMesa(e.target.value)}
                placeholder="Informe o motivo do cancelamento..."
                className="mt-2 min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground mt-1">Os pedidos desta mesa ficarão na aba Pedidos com status Cancelado.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCancelarMesaModalOpen(false)} className="flex-1">Voltar</Button>
              <Button variant="destructive" onClick={handleCancelarMesa} disabled={!motivoValido(motivoCancelamentoMesa)} className="flex-1">
                <XCircle className="w-4 h-4 mr-1" />
                Confirmar Cancelamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cálculo de troco do DINHEIRO (v1.5.518) — este mount atende o
          pagamento aberto DIRETO DO CARD da lista; o da tela de vendas tem o
          seu próprio (o componente tem dois returns). */}
      <PixGerarQrModal
        open={showPixConfirm}
        onOpenChange={(v) => { setShowPixConfirm(v); if (!v) setSelectedPayment((cur) => (/pix_online/i.test(cur || "") ? null : cur)); }}
        total={totalParaFechar}
        onGerar={prepararPixOnline}
      />
      <TrocoModal
        open={showTrocoModal}
        onOpenChange={setShowTrocoModal}
        total={totalParaFechar}
        onConfirm={setCashReceived}
      />

      {/* Pagamento do Fechar mesa aberto direto do card da lista */}
      {!mesaVendasOpen && <FechamentoMesaDialog f={fechamentoProps} />}


      {/* PIX Online (tela principal) — cobrança avulsa do saldo da mesa */}
      <PixCobrancaModal
        open={showPixModal && !mesaVendasOpen}
        onClose={() => { setShowPixModal(false); setPixOrderId(null); setPaymentDialogOpen(true); }}
        pedidoId={pixOrderId}
        amount={pixValor}
        nomeCliente={selectedMesa?.cliente_nome || ""}
        onPago={handlePixConfirmed}
      />

      {/* Dialog Transferir Mesa */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Transferir Mesa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {mesaOrigem && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Mesa de origem</p>
                <p className="font-bold text-lg">Mesa {mesaLabelShort(mesaOrigem)}</p>
                <p className="text-sm">{mesaOrigem.cliente || "Sem cliente"} — R$ {(mesaOrigem.total || 0).toFixed(2).replace('.', ',')}</p>
                {(mesaOrigem.items || []).length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{mesaOrigem.items.filter(i => !i.deleted).length} item(ns)</p>
                )}
              </div>
            )}
            <div>
              <Label className="text-sm font-semibold">Mesa de destino</Label>
              <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 mt-2 max-h-48 overflow-y-auto">
                {mesas
                  .filter(m => m.status === "fechada" && m.id !== mesaOrigem?.id)
                  .sort((a, b) => a.numero - b.numero)
                  .map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMesaDestinoId(m.id)}
                      className={`p-2 rounded-lg border-2 text-center transition-all ${
                        mesaDestinoId === m.id
                          ? 'border-primary bg-primary/10 font-bold'
                          : 'border-muted bg-card hover:border-primary/40'
                      }`}
                    >
                      <span className="text-xs text-muted-foreground">Mesa</span>
                      <span className="block text-lg font-bold">{mesaLabelShort(m)}</span>
                    </button>
                  ))}
              </div>
              {mesas.filter(m => m.status === "fechada" && m.id !== mesaOrigem?.id).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhuma mesa disponível para transferência.</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTransferDialogOpen(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleTransferirMesa} disabled={!mesaDestinoId} className="flex-1">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Transferir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
